import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Star, Calendar as CalendarIcon, CreditCard, BookOpen, AlertCircle, CheckCircle, Globe, User, Smartphone, Wallet, Phone, Users, X, ArrowRight, Sparkles, ChevronDown, DollarSign } from 'lucide-react';

// Type for pricing plan options
type PricingPlan = 'hourly' | 'session' | 'level' | 'monthly';
type LevelOption = 'beginner' | 'intermediate' | 'advanced';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PriceWithUsdBadge } from '@/components/tutor/PriceWithUsdBadge';
import { useToast } from '@/hooks/use-toast';
import { pb, TutorProfile, Booking } from '@/lib/pocketbase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import MockPaymentModal from './MockPaymentModal';
import FrozenTutorModal from './FrozenTutorModal';
import { trackInitiateCheckout } from '@/lib/metaPixel';

import { EmailService } from '@/services/emailService';
import { kashierService, KashierCheckoutResponse } from '@/services/kashierService';
import { useCurrency } from '@/contexts/CurrencyContext';
import { PromoCodeInput } from '@/components/booking/PromoCodeInput';
import { DiscountValidationResult, incrementDiscountUsage } from '@/services/discountCodeService';
import { createTutorEarning } from '@/hooks/useTutorEarnings';
import { countries, getCountryByCode } from '@/utils/countries';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { hasMultipleTimezones, getCountryTimezones, getTimezoneDisplayName } from '@/utils/countryTimezones';
import { CountryCombobox } from '@/components/CountryCombobox';
import { isAdmin } from '@/utils/roleValidation';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { capitalizeSubject } from '@/utils/tutorDataUtils';
import { useActiveDiscount } from '@/hooks/useActiveDiscount';
import { DiscountBanner } from '@/components/DiscountBanner';

// Helper function to ensure student profile exists and update contact info
const ensureStudentProfileExists = async (
  userId: string, 
  email: string, 
  phone: string, 
  country: string
): Promise<void> => {
  try {
    // Check if student profile already exists
    const existingProfiles = await pb.collection('student_profiles').getList(1, 1, {
      filter: `user = "${userId}"`
    });
    
    if (existingProfiles.items.length === 0) {
      // Create new student profile with contact info
      console.log('📝 Creating student profile for user:', userId);
      await pb.collection('student_profiles').create({
        user: userId,
        name: '',
        personalEmail: email,
        phoneNumber: phone,
        country: country,
        bio: '',
        subjects: [],
        learningGoals: '',
        preferredLearningStyle: '',
        parentEmail: '',
        is_suspended: false,
        suspended_at: null,
        emergencyContact: '',
        adminNotes: ''
      });
      console.log('✅ Student profile created successfully');
    } else {
      // Update existing profile with new contact info
      const existingProfile = existingProfiles.items[0];
      console.log('📝 Updating student profile with new contact info:', userId);
      await pb.collection('student_profiles').update(existingProfile.id, {
        personalEmail: email,
        phoneNumber: phone,
        country: country
      });
      console.log('✅ Student profile updated successfully');
    }
  } catch (error) {
    console.error('⚠️ Error checking/creating/updating student profile:', error);
    // Don't throw - we don't want to block booking if profile update fails
  }
};

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutor: TutorProfile | null;
  customPrice?: number; // Optional custom price for local coaching
  isLocalCoach?: boolean; // Flag to identify local fitness coach booking
  localCoachData?: any; // Local coach availability data
  initialSubject?: string; // Optional initial subject selection
}

const BookingModal = ({ isOpen, onClose, tutor, customPrice, isLocalCoach, localCoachData, initialSubject }: BookingModalProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { convertPrice, formatPrice, selectedCurrency, currencies: currencyRates } = useCurrency();
  const { activeDiscount, calculateDiscountedPrice, appliesToType, hasActiveDiscount } = useActiveDiscount();
  const [supportContacts, setSupportContacts] = useState<Array<{ id: string; phone_number: string; label: string }>>([]);

  const egpRate = currencyRates.find((c) => c.code === 'EGP')?.rate || 50;

  const tutorPriceToUsd = (price: number) => {
    const tutorCurrency = tutor?.pricingCurrency || 'USD';
    if (tutorCurrency === 'USD') return price;
    const tutorCurrencyRate = currencyRates.find((c) => c.code === tutorCurrency)?.rate;
    // If rate not found, assume it's already USD
    if (!tutorCurrencyRate) return price;
    return price / tutorCurrencyRate;
  };

  const usdToEgpRounded = (usdPrice: number) => {
    const egp = usdPrice * egpRate;
    // Match CurrencyContext.formatPrice: round to nearest 10 EGP
    return Math.round(egp / 10) * 10;
  };

  const tutorPriceToEgpRounded = (price: number) => usdToEgpRounded(tutorPriceToUsd(price));

  // Format price - convert tutor's price to selected currency
  const formatTutorPrice = (price: number) => {
    if (!price) return formatPrice(0);

    const formatted = formatPrice(tutorPriceToUsd(price));

    // For EGP, convert to Arabic numerals
    if (selectedCurrency.code === 'EGP') {
      return formatted.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    }

    return formatted;
  };

  // Format an EGP amount directly (used for promo-adjusted Kashier totals)
  const formatEGPAmount = (egpAmount: number) => {
    const base = `${Math.round(egpAmount)} ج.م`;
    return selectedCurrency.code === 'EGP'
      ? base.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : base;
  };
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ start: string; end: string } | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string>(initialSubject || '');
  const [studentEmail, setStudentEmail] = useState<string>('');
  const [studentPhone, setStudentPhone] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [studentCountry, setStudentCountry] = useState<string>('EG');
  const [studentTimezoneOverride, setStudentTimezoneOverride] = useState<string>('');
  const [isBooking, setIsBooking] = useState(false);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [isCreatingHPPPayment, setIsCreatingHPPPayment] = useState(false);
  const [isCreatingIframePayment, setIsCreatingIframePayment] = useState(false);
  const [showMockPayment, setShowMockPayment] = useState(false);

  // Reset payment states when modal closes or opens
  useEffect(() => {
    if (!isOpen) {
      // Reset all payment-related states when modal closes
      setIsCreatingPayment(false);
      setIsCreatingHPPPayment(false);
      setIsCreatingIframePayment(false);
      setAppliedPromoDiscount(null);
    }
  }, [isOpen]);
  const [bookedSlots, setBookedSlots] = useState<{ [key: string]: { start: string; end: string }[] }>({});
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [allBookings, setAllBookings] = useState<any[]>([]);
  
  // Group booking states
  const [bookingType, setBookingType] = useState<'individual' | 'group'>('individual');
  const [groupSize, setGroupSize] = useState<number>(2);
  const [groupLeaderEmail, setGroupLeaderEmail] = useState<string>('');
  const [groupLeaderPhone, setGroupLeaderPhone] = useState<string>('');
  const [memberEmails, setMemberEmails] = useState<string[]>(['']);
  const [memberPhones, setMemberPhones] = useState<string[]>(['']);
  
  // Floating login button state
  const [showFloatingLogin, setShowFloatingLogin] = useState(false);
  const contactInfoRef = useRef<HTMLDivElement>(null);
  
  // Promo code state
  const [appliedPromoDiscount, setAppliedPromoDiscount] = useState<DiscountValidationResult | null>(null);

  // Pricing plan state
  const [selectedPricingPlan, setSelectedPricingPlan] = useState<PricingPlan>('hourly');
  const [selectedLevel, setSelectedLevel] = useState<LevelOption>('beginner');

  // Get available pricing options for this tutor
  const availablePricingOptions = useMemo(() => {
    if (!tutor) return [];
    
    const options: { value: PricingPlan; label: string; labelAr: string; price: number; priceLabel: string; priceLabelAr: string }[] = [];
    
    // Hourly rate
    if (tutor.hourlyRate && tutor.hourlyRate > 0) {
      options.push({
        value: 'hourly',
        label: 'Per Hour',
        labelAr: 'بالساعة',
        price: tutor.hourlyRate,
        priceLabel: '/hour',
        priceLabelAr: '/ساعة'
      });
    }
    
    // Session rate
    if (tutor.sessionRate && tutor.sessionRate > 0) {
      options.push({
        value: 'session',
        label: 'Per Session',
        labelAr: 'بالجلسة',
        price: tutor.sessionRate,
        priceLabel: '/session',
        priceLabelAr: '/جلسة'
      });
    }
    
    // Level rates
    if (tutor.levelRates) {
      const levelRates = tutor.levelRates;
      const hasValidLevelRates = Object.values(levelRates).some((rate: any) => rate && rate > 0);
      if (hasValidLevelRates) {
        // Use the first available level rate for display
        const firstValidRate = levelRates.beginner || levelRates.intermediate || levelRates.advanced || 0;
        options.push({
          value: 'level',
          label: 'By Level',
          labelAr: 'حسب المستوى',
          price: firstValidRate,
          priceLabel: '/level',
          priceLabelAr: '/مستوى'
        });
      }
    }
    
    // Monthly rate
    if (tutor.monthlyRate && tutor.monthlyRate > 0) {
      options.push({
        value: 'monthly',
        label: 'Monthly',
        labelAr: 'اشتراك شهري',
        price: tutor.monthlyRate,
        priceLabel: '/month',
        priceLabelAr: '/شهر'
      });
    }
    
    return options;
  }, [tutor]);

  // Set default pricing plan when tutor changes
  useEffect(() => {
    if (availablePricingOptions.length > 0) {
      // Default to hourly if available, otherwise first option
      const hasHourly = availablePricingOptions.some(o => o.value === 'hourly');
      setSelectedPricingPlan(hasHourly ? 'hourly' : availablePricingOptions[0].value);
    }
  }, [availablePricingOptions]);

  // Get price based on selected pricing plan
  const getSelectedPlanPrice = useCallback(() => {
    if (!tutor) return 0;
    
    switch (selectedPricingPlan) {
      case 'hourly':
        return tutor.hourlyRate || 0;
      case 'session':
        return tutor.sessionRate || 0;
      case 'level':
        const levelRates = tutor.levelRates;
        if (!levelRates) return 0;
        return levelRates[selectedLevel] || levelRates.beginner || levelRates.intermediate || levelRates.advanced || 0;
      case 'monthly':
        return tutor.monthlyRate || 0;
      default:
        return tutor.hourlyRate || 0;
    }
  }, [tutor, selectedPricingPlan, selectedLevel]);

  // Fetch support contacts
  useEffect(() => {
    const fetchSupportContacts = async () => {
      try {
        const records = await pb.collection('support_contacts').getFullList({
          filter: 'is_active = true',
          sort: 'display_order,created'
        });
        setSupportContacts(records.map(r => ({
          id: r.id,
          phone_number: r.phone_number,
          label: r.label
        })));
      } catch (error) {
        console.error('Error fetching support contacts:', error);
      }
    };

    if (isOpen) {
      fetchSupportContacts();
    }
  }, [isOpen]);

  // Fetch and pre-fill user profile data when modal opens
  useEffect(() => {
    const fetchUserProfileData = async () => {
      if (isOpen && user?.id) {
        try {
          // Fetch from tutors collection if user is a tutor
          if (user.role === 'tutor') {
            try {
              const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
                filter: `user = "${user.id}"`
              });
              
              if (tutorProfiles.items.length > 0) {
                const profile = tutorProfiles.items[0];
                console.log('📋 Found tutor profile, pre-filling data:', profile);
                
                // Pre-fill from tutor profile
                if (profile.personalEmail) {
                  setStudentEmail(profile.personalEmail);
                  setGroupLeaderEmail(profile.personalEmail);
                }
                if (profile.phoneNumber) {
                  setStudentPhone(profile.phoneNumber);
                  setGroupLeaderPhone(profile.phoneNumber);
                }
                if (profile.country) {
                  setStudentCountry(profile.country);
                }
                
                return; // Profile found, no need to fetch from users collection
              }
            } catch (profileError) {
              console.log('No tutor profile found, falling back to user data');
            }
          }
          
          // Fetch from student_profiles if user is a student
          if (user.role === 'student') {
            try {
              const studentProfiles = await pb.collection('student_profiles').getList(1, 1, {
                filter: `user = "${user.id}"`
              });
              
              if (studentProfiles.items.length > 0) {
                const profile = studentProfiles.items[0];
                console.log('📋 Found student profile, pre-filling data:', profile);
                
                // Pre-fill from student profile
                if (profile.personalEmail) {
                  setStudentEmail(profile.personalEmail);
                  setGroupLeaderEmail(profile.personalEmail);
                }
                if (profile.phoneNumber) {
                  setStudentPhone(profile.phoneNumber);
                  setGroupLeaderPhone(profile.phoneNumber);
                }
                if (profile.country) {
                  setStudentCountry(profile.country);
                }
                
                return; // Profile found, no need to fetch from users collection
              }
            } catch (profileError) {
              console.log('No student profile found, falling back to user data');
            }
          }
          
          // Fallback: Fetch the user's email from users collection
          const userData = await pb.collection('users').getOne(user.id);
          if (userData.email) {
            console.log('📧 Pre-filling email from user account:', userData.email);
            setStudentEmail(userData.email);
            setGroupLeaderEmail(userData.email);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // If fetch fails, leave the fields empty for manual entry
        }
      }
    };

    fetchUserProfileData();
  }, [isOpen, user?.id, user?.role]);

  // Main five subjects to prioritize
  const MAIN_SUBJECTS = ['english', 'music', 'programming', 'fitness', 'travel'];
  
  // Get the default subject for smart selection
  const getDefaultSubject = (subjects: string[] | undefined): string => {
    if (!subjects || subjects.length === 0) return '';
    if (subjects.length === 1) return subjects[0];
    
    // Check if any subject matches main five (case-insensitive)
    const mainSubject = subjects.find(s => 
      MAIN_SUBJECTS.includes(s.toLowerCase())
    );
    if (mainSubject) return mainSubject;
    
    // Default to first subject
    return subjects[0];
  };

  // Update selected subject when modal opens or initialSubject changes
  useEffect(() => {
    if (isOpen && initialSubject) {
      console.log('🎵 Setting initial subject for booking modal:', initialSubject);
      setSelectedSubject(initialSubject);
    } else if (isOpen && tutor?.subjects) {
      const defaultSubject = getDefaultSubject(tutor.subjects);
      console.log('🎵 Smart default subject selected:', defaultSubject);
      setSelectedSubject(defaultSubject);
    } else if (isOpen && !initialSubject) {
      setSelectedSubject('');
    }
  }, [isOpen, initialSubject, tutor?.subjects]);

  // Fetch booked slots when tutor changes or modal opens
  const fetchBookedSlots = useCallback(async () => {
    if (!tutor?.id) {
      console.log('❌ fetchBookedSlots: No tutor ID available');
      return;
    }
    
    console.log('🔍 fetchBookedSlots: Starting to fetch booked slots for tutor:', tutor.id);
    
    setIsLoadingBookings(true);
    try {
      // Get current date for filtering future bookings
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch all booked slots for this tutor from today onwards
      // Note: PocketBase bookings collection List rule must allow viewing by tutorId filter
      const bookings = await pb.collection('bookings').getFullList({
        filter: `tutorId="${tutor.id}" && date>="${today}" && status!="cancelled" && status!="rejected"`
      });

      console.log('📊 fetchBookedSlots: Future active bookings:', bookings.length);

      // Group bookings by date
      const slotsByDate: { [key: string]: { start: string; end: string }[] } = {};
      
      bookings.forEach(booking => {
        const dateKey = booking.date;
        if (!slotsByDate[dateKey]) {
          slotsByDate[dateKey] = [];
        }
        slotsByDate[dateKey].push({
          start: booking.startTime,
          end: booking.endTime
        });
      });

      console.log('🗓️ fetchBookedSlots: Slots grouped by date:', slotsByDate);
      setBookedSlots(slotsByDate);
    } catch (error) {
      console.error('❌ fetchBookedSlots: Error fetching booked slots:', error);
      setBookedSlots({});
    } finally {
      setIsLoadingBookings(false);
    }
  }, [tutor?.id]);

  // Scroll detection for floating login button
  useEffect(() => {
    if (!isOpen || user) {
      setShowFloatingLogin(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show button when contact section is out of view for logged-out users
        setShowFloatingLogin(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '-100px 0px 0px 0px'
      }
    );

    if (contactInfoRef.current) {
      observer.observe(contactInfoRef.current);
    }

    return () => {
      if (contactInfoRef.current) {
        observer.unobserve(contactInfoRef.current);
      }
    };
  }, [isOpen, user]);

  // Re-validate phone when country changes
  useEffect(() => {
    if (studentPhone.trim() && studentCountry) {
      const result = validatePhoneByCountry(studentPhone, studentCountry);
      if (result.valid) {
        setPhoneError('');
      } else {
        setPhoneError(result.message || 'Invalid phone number');
      }
    } else {
      setPhoneError('');
    }
  }, [studentCountry, studentPhone]);

  useEffect(() => {
    console.log('🎬 BookingModal useEffect triggered:', { isOpen, tutorId: tutor?.id, isLoadingBookings });
    if (isOpen && tutor?.id) {
      console.log('✅ Conditions met, calling fetchBookedSlots...');
      // Clear previous selections when modal opens
      setSelectedDate(undefined);
      setSelectedTimeSlot(null);
      fetchBookedSlots();
    } else {
      console.log('❌ Conditions not met for fetchBookedSlots:', { 
        isOpen, 
        hasTutor: !!tutor?.id, 
        isLoadingBookings 
      });
      
      // If modal is closed, clear booking data
      if (!isOpen) {
        console.log('🧹 Modal closed, clearing booked slots and selections');
        setBookedSlots({});
        setSelectedDate(undefined);
        setSelectedTimeSlot(null);
      }
    }
  }, [isOpen, tutor?.id]);

  // Get student's timezone from selected country
  const getStudentTimezone = () => {
    // If student manually selected a timezone for a multi-timezone country, use that
    if (studentTimezoneOverride) return studentTimezoneOverride;
    
    if (!studentCountry) return Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = getCountryByCode(studentCountry);
    return country?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const studentTimezone = getStudentTimezone();

  // Get the earliest bookable date (3 days from now)
  const getEarliestBookableDate = () => {
    const earliestDate = new Date();
    earliestDate.setDate(earliestDate.getDate() + 3);
    earliestDate.setHours(0, 0, 0, 0);
    return earliestDate;
  };

  // Get the effective hourly rate (custom price takes precedence, then selected plan price, then site-wide discount)
  const getEffectiveHourlyRate = () => {
    let rate = customPrice !== undefined ? customPrice : getSelectedPlanPrice();
    // Apply site-wide discount if active and applies to bookings
    if (hasActiveDiscount && appliesToType('bookings')) {
      rate = calculateDiscountedPrice(rate);
    }
    // Promo discount is handled separately in EGP - don't apply here to avoid currency confusion
    // The final EGP price calculation will apply the promo discount
    return rate;
  };

  // Get the effective hourly rate in EGP with promo discount applied
  const getEffectiveHourlyRateInEGPWithPromo = () => {
    let egpRate = getEffectiveHourlyRateInEGP();
    if (appliedPromoDiscount?.isValid && appliedPromoDiscount.discountAmount) {
      egpRate = Math.max(0, egpRate - appliedPromoDiscount.discountAmount);
    }
    return egpRate;
  };

  // Get original price before any discount (for strikethrough display)
  const getOriginalHourlyRate = () => {
    return customPrice !== undefined ? customPrice : getSelectedPlanPrice();
  };

  // Get promo code discount amount (in EGP - from validation result)
  const getPromoDiscountAmount = () => {
    if (!appliedPromoDiscount?.isValid || !appliedPromoDiscount.discountAmount) return 0;
    return appliedPromoDiscount.discountAmount;
  };

  // Get promo code discount amount converted back to tutor's currency (for display)
  const getPromoDiscountInTutorCurrency = () => {
    const discountEGP = getPromoDiscountAmount();
    if (!discountEGP) return 0;
    
    const tutorCurrency = tutor?.pricingCurrency || 'USD';
    if (tutorCurrency === 'EGP') return discountEGP;
    
    // Convert EGP back to USD first
    const discountUSD = discountEGP / 50;
    
    if (tutorCurrency === 'USD') return discountUSD;
    
    // Convert USD to tutor's currency
    const conversionRates: { [key: string]: number } = {
      'EUR': 1.1,
      'GBP': 1.27,
      'CAD': 0.72,
      'AUD': 0.65,
      'JPY': 0.0067,
      'CNY': 0.14,
      'INR': 0.012,
      'SAR': 0.27,
      'AED': 0.27
    };
    return discountUSD * (conversionRates[tutorCurrency] || 1);
  };

  // Check if discount should be shown
  const showDiscountedPrice = (hasActiveDiscount && appliesToType('bookings') && getOriginalHourlyRate() !== getEffectiveHourlyRate()) || appliedPromoDiscount?.isValid;

  // Convert tutor's price to EGP for Kashier payment (uses discounted price)
  const getEffectiveHourlyRateInEGP = () => {
    const basePrice = getEffectiveHourlyRate(); // in tutor currency
    if (!basePrice) return 0;
    return tutorPriceToEgpRounded(basePrice);
  };
  // Get effective hourly rate in EGP (for promo code validation)
  const getEffectiveHourlyRateInEGPBeforePromo = () => {
    return getEffectiveHourlyRateInEGP();
  };

  // Get tutor's timezone based on country (fallback to UTC)
  const getTutorTimezone = () => {
    // If tutor has a specific timezone set, use that
    if (tutor?.timezone) {
      return tutor.timezone;
    }
    
    // Otherwise fall back to country-based timezone
    if (!tutor?.country) return 'UTC';
    
    // Common country to timezone mappings (fallback)
    const countryTimezones: { [key: string]: string } = {
      'US': 'America/New_York',
      'CA': 'America/Toronto', 
      'GB': 'Europe/London',
      'UK': 'Europe/London',
      'DE': 'Europe/Berlin',
      'FR': 'Europe/Paris',
      'ES': 'Europe/Madrid',
      'IT': 'Europe/Rome',
      'AU': 'Australia/Sydney',
      'NZ': 'Pacific/Auckland',
      'JP': 'Asia/Tokyo',
      'CN': 'Asia/Shanghai',
      'IN': 'Asia/Kolkata',
      'SG': 'Asia/Singapore',
      'AE': 'Asia/Dubai',
      'EG': 'Africa/Cairo',
      'ZA': 'Africa/Johannesburg',
      'BR': 'America/Sao_Paulo',
      'MX': 'America/Mexico_City',
      'AR': 'America/Argentina/Buenos_Aires',
      'RU': 'Europe/Moscow',
      'FI': 'Europe/Helsinki',
      'NO': 'Europe/Oslo',
      'SE': 'Europe/Stockholm',
      'DK': 'Europe/Copenhagen',
      'NL': 'Europe/Amsterdam',
      'BE': 'Europe/Brussels',
      'CH': 'Europe/Zurich',
      'AT': 'Europe/Vienna',
      'PL': 'Europe/Warsaw',
      'CZ': 'Europe/Prague',
      'HU': 'Europe/Budapest',
      'PT': 'Europe/Lisbon',
      'GR': 'Europe/Athens',
      'TR': 'Europe/Istanbul',
      'IL': 'Asia/Jerusalem',
      'KR': 'Asia/Seoul',
      'TH': 'Asia/Bangkok',
      'VN': 'Asia/Ho_Chi_Minh',
      'MY': 'Asia/Kuala_Lumpur',
      'PH': 'Asia/Manila',
      'ID': 'Asia/Jakarta'
    };
    
    return countryTimezones[tutor.country] || 'UTC';
  };

  const tutorTimezone = getTutorTimezone();

  // Convert tutor's local time to student's local timezone
  const convertToStudentTime = (time: string, selectedDate: Date) => {
    if (!time || !selectedDate || !studentCountry) return time;
    
    // If timezones are the same, no conversion needed
    if (tutorTimezone === studentTimezone) return time;
    
    try {
      // Create a date string in tutor's timezone
      const dateString = selectedDate.toISOString().split('T')[0];
      const tutorDateTimeString = `${dateString}T${time}:00`;
      
      console.log('🌍 Timezone Conversion Debug:', {
        originalTime: time,
        tutorTimezone,
        studentTimezone,
        tutorDateTimeString,
        selectedDate: dateString
      });
      
      // Convert from tutor's timezone to UTC first
      const tutorDate = fromZonedTime(tutorDateTimeString, tutorTimezone);
      console.log('🌍 After fromZonedTime (UTC):', tutorDate.toISOString());
      
      // Then convert from UTC to student's timezone
      const studentDate = toZonedTime(tutorDate, studentTimezone);
      console.log('🌍 After toZonedTime (Student TZ):', studentDate.toISOString(), studentDate.toString());
      
      // Format the result
      const hours = studentDate.getHours().toString().padStart(2, '0');
      const minutes = studentDate.getMinutes().toString().padStart(2, '0');
      
      const result = `${hours}:${minutes}`;
      console.log('🌍 Final converted time:', result);
      
      return result;
    } catch (error) {
      console.error('Timezone conversion error:', error);
      return time;
    }
  };

  // Get the local time display for selected slot
  const getLocalTimeDisplay = () => {
    if (!selectedTimeSlot || !selectedDate || !studentCountry) return '';
    
    const studentStart = convertToStudentTime(selectedTimeSlot.start, selectedDate);
    const studentEnd = convertToStudentTime(selectedTimeSlot.end, selectedDate);
    
    if (!studentStart || !studentEnd) return '';
    
    // Show both tutor's timezone and student's timezone if different
    if (tutorTimezone === studentTimezone) {
      return `${selectedTimeSlot.start} - ${selectedTimeSlot.end} (${tutorTimezone})`;
    }
    
    return `${studentStart} - ${studentEnd} (${studentTimezone})`;
  };

  const getTimezoneInfo = () => {
    if (!studentCountry) return '';
    if (tutorTimezone === studentTimezone) {
      return `Times shown in ${tutorTimezone}`;
    }
    return `Tutor's time: ${selectedTimeSlot?.start} - ${selectedTimeSlot?.end} (${tutorTimezone})`;
  };

  // Early return if tutor is null - don't render anything
  if (!tutor) {
    return null;
  }

  // Only log when modal is actually open and has valid data
  if (isOpen) {
    console.log('=== 🚨 BOOKING MODAL DEBUG CHECK 🚨 ===');
    console.log('🔄 This log confirms the updated BookingModal is loaded');
    console.log('BookingModal - Current user:', user);
    console.log('BookingModal - User authenticated:', !!user);
    console.log('BookingModal - Tutor profile:', tutor);
  }

  const getTutorName = () => {
    return tutor.expand?.user?.name || 'Unknown Tutor';
  };

  const getProfilePictureUrl = () => {
    if (!tutor?.profilePicture) return '';
    return pb.files.getURL(tutor, tutor.profilePicture);
  };

  const getAvailableTimeSlotsForDate = (date: Date) => {
    // Convert date to multiple possible formats for comparison
    const dateKey1 = date.toISOString().split('T')[0]; // "2025-09-11"
    const dateKey2 = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString(); // Full ISO with timezone adjustment
    const dateKey3 = date.toISOString(); // Full ISO string
    
    console.log('🎯 getAvailableTimeSlotsForDate: Checking date:', {
      inputDate: date,
      key1: dateKey1,
      key2: dateKey2,
      key3: dateKey3,
      availableKeys: Object.keys(bookedSlots)
    });
    
    // Try to find booked slots using different date formats
    let bookedSlotsForDate: { start: string; end: string }[] = [];
    
    // Check each possible format
    for (const key of Object.keys(bookedSlots)) {
      const keyDate = key.split(' ')[0]; // Get just the date part "2025-09-11"
      if (keyDate === dateKey1) {
        bookedSlotsForDate = bookedSlots[key];
        console.log('🎯 Found booked slots using format match:', key, bookedSlotsForDate);
        break;
      }
    }
    
    // If no exact match, try partial matching
    if (bookedSlotsForDate.length === 0) {
      for (const [key, slots] of Object.entries(bookedSlots)) {
        if (key.includes(dateKey1)) {
          bookedSlotsForDate = slots;
          console.log('🎯 Found booked slots using partial match:', key, bookedSlotsForDate);
          break;
        }
      }
    }
    
    console.log('🎯 getAvailableTimeSlotsForDate: Final booked slots for this date:', bookedSlotsForDate);
    
    let allTimeSlots: { start: string; end: string }[] = [];
    
    if (isLocalCoach && localCoachData) {
      // Use local coach availability
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const dayAvailability = localCoachData.availability?.[dayName];
      
      if (dayAvailability?.isAvailable && dayAvailability?.timeSlots) {
        allTimeSlots = dayAvailability.timeSlots.map((slot: any) => ({
          start: slot.start,
          end: slot.end
        }));
      }
    } else {
      // Regular tutor availability
      if (!tutor.availability?.timeSlots) return [];
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const daySlot = tutor.availability.timeSlots.find(slot => slot.day === dayName);
      
      allTimeSlots = daySlot?.slots || [];
    }

    console.log('🎯 getAvailableTimeSlotsForDate: All possible slots:', allTimeSlots);

    // Mark slots as booked/available instead of filtering them out
    const slotsWithBookingStatus = allTimeSlots.map(slot => {
      const isBooked = bookedSlotsForDate.some(bookedSlot => {
        // Normalize time formats for comparison (remove any extra spaces, ensure consistent format)
        const normalizeTime = (time: string) => time.trim().replace(/\s+/g, '');
        
        const slotStart = normalizeTime(slot.start);
        const slotEnd = normalizeTime(slot.end);
        const bookedStart = normalizeTime(bookedSlot.start);
        const bookedEnd = normalizeTime(bookedSlot.end);
        
        const startMatch = slotStart === bookedStart;
        const endMatch = slotEnd === bookedEnd;
        
        console.log(`🔍 Comparing slot "${slotStart}-${slotEnd}" with booked "${bookedStart}-${bookedEnd}"`);
        console.log(`🔍 Start match: ${startMatch}, End match: ${endMatch}`);
        
        return startMatch && endMatch;
      });
      console.log(`🎯 Slot ${slot.start}-${slot.end}: ${isBooked ? 'BOOKED ❌' : 'AVAILABLE ✅'}`);
      return {
        ...slot,
        isBooked
      };
    });

    console.log('🎯 getAvailableTimeSlotsForDate: All slots with booking status:', slotsWithBookingStatus);
    return slotsWithBookingStatus;
  };

  // Check if a date has tutor availability (regardless of bookings)
  const hasTutorAvailability = (date: Date) => {
    if (isLocalCoach && localCoachData) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const dayAvailability = localCoachData.availability?.[dayName];
      return dayAvailability?.isAvailable && dayAvailability?.timeSlots?.length > 0;
    }

    // Regular tutor availability check
    if (!tutor.availability?.timeSlots) {
      return false;
    }
    
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const daySlot = tutor.availability.timeSlots.find(slot => slot.day === dayName);
    return daySlot?.slots?.length > 0;
  };

  // Check if a date has available (non-booked) slots
  const isDateAvailable = (date: Date) => {
    console.log('🗓️ Checking date availability for:', date.toLocaleDateString());
    
    // Prevent booking within 3 days - date must be 3 days from now or later
    const earliestDate = getEarliestBookableDate();
    if (date < earliestDate) {
      console.log('🗓️ Date is within blocked 3-day period - bookings not allowed');
      return false;
    }
    
    if (!hasTutorAvailability(date)) {
      console.log('🗓️ No tutor availability for this day');
      return false;
    }
    
    // Check if all slots for this date are booked
    const allSlots = getAvailableTimeSlotsForDate(date);
    const hasAvailableSlots = allSlots.some(slot => !slot.isBooked);
    
    console.log('🗓️ All slots for date:', allSlots);
    console.log('🗓️ Has available (non-booked) slots:', hasAvailableSlots);
    
    return hasAvailableSlots;
  };

  // Check if a date is fully booked (has tutor availability but all slots booked)
  const isDateFullyBooked = (date: Date) => {
    // Don't show blocked dates as fully booked, they should just be disabled
    const earliestDate = getEarliestBookableDate();
    if (date < earliestDate) {
      return false;
    }
    
    if (!hasTutorAvailability(date)) {
      return false;
    }
    
    const allSlots = getAvailableTimeSlotsForDate(date);
    const hasAvailableSlots = allSlots.some(slot => !slot.isBooked);
    
    return allSlots.length > 0 && !hasAvailableSlots;
  };

  // Group booking helper functions
  const handleAddMemberEmail = () => {
    setMemberEmails([...memberEmails, '']);
    setMemberPhones([...memberPhones, '']);
  };

  const handleRemoveMemberEmail = (index: number) => {
    const updatedEmails = memberEmails.filter((_, i) => i !== index);
    const updatedPhones = memberPhones.filter((_, i) => i !== index);
    setMemberEmails(updatedEmails.length > 0 ? updatedEmails : ['']);
    setMemberPhones(updatedPhones.length > 0 ? updatedPhones : ['']);
  };

  const handleMemberEmailChange = (index: number, value: string) => {
    const updated = [...memberEmails];
    updated[index] = value;
    setMemberEmails(updated);
  };

  const handleMemberPhoneChange = (index: number, value: string) => {
    const updated = [...memberPhones];
    updated[index] = value;
    setMemberPhones(updated);
  };

  const generateInviteToken = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };
  
  // Calculate group booking price: (hourly rate × group size) × 0.7 (30% discount)
  const calculateGroupPrice = () => {
    const baseRate = getEffectiveHourlyRate();
    const totalBeforeDiscount = baseRate * groupSize;
    const discountedPrice = totalBeforeDiscount * 0.7; // 30% off
    return {
      baseRate,
      totalBeforeDiscount,
      discountedPrice,
      savings: totalBeforeDiscount - discountedPrice
    };
  };

  // Helper function to get phone code for country
  const getPhoneCode = (countryCode: string): string => {
    const phoneCodes: Record<string, string> = {
      'EG': '+2',
      'US': '+1',
      'GB': '+44',
      'CA': '+1',
      'AU': '+61',
      'DE': '+49',
      'FR': '+33',
      'ES': '+34',
      'IT': '+39',
      'NL': '+31',
      'JP': '+81',
      'CN': '+86',
      'IN': '+91',
      'BR': '+55',
      'MX': '+52',
      'ZA': '+27',
      'AE': '+971',
      'SG': '+65',
      'NZ': '+64',
      'SE': '+46',
      'NO': '+47',
      'DK': '+45',
      'FI': '+358',
      'PL': '+48',
      'GR': '+30',
      'PT': '+351',
      'IE': '+353',
      'AT': '+43',
      'BE': '+32',
      'CH': '+41',
      'CZ': '+420',
      'RU': '+7',
      'TR': '+90',
      'SA': '+966',
      'KR': '+82',
      'TH': '+66',
      'MY': '+60',
      'PH': '+63',
      'ID': '+62',
      'VN': '+84',
      'AR': '+54',
      'CL': '+56',
      'CO': '+57',
      'PE': '+51',
      'IL': '+972',
      'HU': '+36',
    };
    return phoneCodes[countryCode] || '+1';
  };

  // Phone validation helper focused on Egypt (EG) and USA (US),
  // with a generic fallback using libphonenumber-js for other countries
  const validatePhoneByCountry = (value: string, countryCode: string) => {
    const digits = value.replace(/\D/g, '');
    const country = getCountryByCode(countryCode);

    if (!digits) {
      return {
        valid: false,
        message: `Please enter a phone number for ${country?.name || 'selected country'}`,
      };
    }

    if (countryCode === 'EG') {
      // Egypt: only 01XXXXXXXXX (11 digits starting with 0)
      if (digits.startsWith('0') && digits.length === 11) {
        return { valid: true };
      }
      return {
        valid: false,
        message: 'For Egypt مصر, use 11 digits starting with 0 (e.g., 01XX XXX XXXX)',
      };
    }

    if (countryCode === 'US') {
      // USA: 10-digit local or 1 + 10 digits with country code
      if ((digits.length === 10) || (digits.length === 11 && digits.startsWith('1'))) {
        return { valid: true };
      }
      return {
        valid: false,
        message: 'For United States, use a 10-digit number or +1 followed by 10 digits.',
      };
    }

    // Fallback: use libphonenumber-js for other countries
    try {
      const isValid = isValidPhoneNumber(value, countryCode as any);
      if (isValid) {
        return { valid: true };
      }
      return {
        valid: false,
        message: `Invalid phone number for ${country?.name || 'selected country'}`,
      };
    } catch (error) {
      return {
        valid: false,
        message: `Invalid format. Please include country code for ${country?.name || 'selected country'}`,
      };
    }
  };
  const getValidationStatus = () => {
    const missingFields: string[] = [];
    
    if (!user) {
      return { isValid: false, missingFields: ['authentication'] };
    }
    
    if (!selectedSubject) missingFields.push('subject');
    if (!studentCountry) missingFields.push('country');
    
    // If country has multiple timezones, require timezone selection
    if (studentCountry && hasMultipleTimezones(studentCountry) && !studentTimezoneOverride) {
      missingFields.push('timezone');
    }
    
    if (!selectedDate) missingFields.push('date');
    if (!selectedTimeSlot) missingFields.push('time slot');
    
    // Different validation for group vs individual bookings
    if (bookingType === 'group') {
      if (!groupLeaderEmail.trim()) missingFields.push('group leader email');
      if (!groupLeaderPhone.trim()) missingFields.push('group leader phone');
      
      const validEmails = memberEmails.filter(e => e.trim());
      const requiredEmails = groupSize - 1; // Exclude group leader from count
      if (validEmails.length === 0) missingFields.push('at least one member email');
      if (validEmails.length !== requiredEmails) missingFields.push(`${requiredEmails} member email${requiredEmails !== 1 ? 's' : ''}`);
    } else {
      if (!studentEmail.trim()) missingFields.push('email');
      if (!studentPhone.trim()) missingFields.push('phone number');
      if (phoneError) missingFields.push('valid phone number');
    }
    
    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };

  const validateBookingForm = () => {
    const { isValid, missingFields } = getValidationStatus();
    
    if (!isValid) {
      const fieldsList = missingFields.join(', ');
      toast({
        title: "Incomplete Booking Form",
        description: `Please complete the following: ${fieldsList}`,
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleMockPaymentComplete = async (mockBookingId: string) => {
    if (!validateBookingForm()) return;

    setIsBooking(true);
    
    try {
      // Check if slot is still available before completing booking
      const dateKey = selectedDate!.toISOString().split('T')[0];
      const bookedSlotsForDate = bookedSlots[dateKey] || [];
      
      const isSlotStillAvailable = !bookedSlotsForDate.some(bookedSlot => 
        selectedTimeSlot!.start === bookedSlot.start && selectedTimeSlot!.end === bookedSlot.end
      );

      if (!isSlotStillAvailable) {
        toast({
          title: "Slot No Longer Available",
          description: "This time slot has been booked by another student. Please select a different slot.",
          variant: "destructive"
        });
        setSelectedTimeSlot(null);
        await fetchBookedSlots(); // Refresh the available slots
        return;
      }

      const formattedDate = selectedDate!.toISOString().split('T')[0];
      
      const bookingData = {
        studentId: user!.id,
        tutorId: tutor.id,
        date: formattedDate,
        startTime: selectedTimeSlot!.start,
        endTime: selectedTimeSlot!.end,
        status: 'pending',
        subject: selectedSubject,
        studentEmail: studentEmail.trim(),
        studentPhone: studentPhone.trim(),
        studentCountry: studentCountry,
        studentTimezone: studentTimezone, // Store the actual timezone used
        notes: '',
        paymentStatus: 'paid',
        price: getEffectiveHourlyRate(),
        pricingPlan: selectedPricingPlan,
        pricingLevel: selectedPricingPlan === 'level' ? selectedLevel : null
      };

      console.log('Creating booking with mock payment:', bookingData);
      
      // Ensure student profile exists before creating booking
      if (user?.role === 'student') {
        await ensureStudentProfileExists(user.id, studentEmail.trim(), studentPhone.trim(), studentCountry);
      }

      const createdBooking = await pb.collection('bookings').create(bookingData);

      // Create payment_sessions record for manual/mock payment
      try {
        const paymentSessionData = {
          orderId: `booking_${createdBooking.id}`,
          bookingId_: createdBooking.id,
          amount: getEffectiveHourlyRate(),
          currency: 'EGP',
          status: 'completed',
          studentId: user!.id,
          source: 'manual', // Manual/mock payment source
          bookingDetails: JSON.stringify({
            tutorName: getTutorName(),
            subject: selectedSubject,
            date: formattedDate,
            time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`
          })
        };
        await pb.collection('payment_sessions').create(paymentSessionData);
        console.log('✅ Payment session created for manual booking');
      } catch (paymentError) {
        console.warn('⚠️ Failed to create payment session record:', paymentError);
        // Don't fail the booking if payment session creation fails
      }

      // Create tutor earnings record
      try {
        const tutorEmail = tutor.personalEmail || tutor.expand?.user?.email || tutor.email || '';
        const tutorUserId = tutor.user || tutor.expand?.user?.id; // Get user ID for relation
        await createTutorEarning(
          tutor.id,
          tutor.name || 'Tutor',
          tutorEmail,
          createdBooking.id,
          user.name || 'Student',
          selectedSubject,
          selectedDate!.toISOString(),
          getEffectiveHourlyRateInEGP(), // Use EGP converted price
          'regular',
          tutorUserId // Pass user ID for the relation field
        );
        console.log('✅ Tutor earnings record created');
      } catch (earningsError) {
        console.warn('⚠️ Failed to create tutor earnings record:', earningsError);
      }

      // Send booking notification emails
      try {
        console.log('📧 Sending booking notification emails...');
        
        // Debug tutor data structure
        console.log('🔍 Debug tutor data:', tutor);
        console.log('🔍 Tutor expand:', tutor.expand);
        console.log('🔍 Tutor user:', tutor.expand?.user);
        console.log('🔍 Tutor email options:', {
          expandUserEmail: tutor.expand?.user?.email,
          tutorEmail: tutor.email,
          userId: tutor.user
        });
        
        const bookingEmailData = {
          studentName: user.name || 'Student',
          studentEmail: studentEmail.trim(),
          studentPhone: studentPhone.trim(),
          tutorName: tutor.expand?.user?.name || tutor.name || 'Tutor',
          tutorEmail: tutor.personalEmail || tutor.expand?.user?.email || tutor.email || '',
          tutorPhone: tutor.phoneNumber,
          subject: selectedSubject,
          date: selectedDate!.toLocaleDateString(),
          time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`,
          status: 'pending',
          tutorTimezone: tutor.timezone || '',
          studentTimezone: studentTimezone || '',
          startTime: selectedTimeSlot!.start,
          endTime: selectedTimeSlot!.end
        };
        
        const emailResult = await EmailService.sendBookingNotification(bookingEmailData, 'regular');
        
        if (emailResult.success) {
          console.log('✅ Booking emails sent successfully:', emailResult.results);
        } else {
          console.warn('⚠️ Some booking emails failed:', emailResult.message);
        }
      } catch (emailError) {
        console.error('📧 Email error:', emailError);
      }

      // Refresh booked slots immediately
      await fetchBookedSlots();
      
      toast({
        title: "Booking Submitted!",
        description: `Your ${selectedSubject} session request with ${getTutorName()} has been submitted for ${selectedDate!.toLocaleDateString()} at ${selectedTimeSlot!.start}. Waiting for tutor confirmation.`,
      });

      navigate(`/booking-confirmation?bookingId=${createdBooking.id}`);
      onClose();
    } catch (error: any) {
      toast({
        title: "Booking Error",
        description: error.message || "Failed to create booking",
        variant: "destructive"
      });
    } finally {
      setIsBooking(false);
    }
  };

  const testServerConnectivity = async () => {
    console.log('=== SERVER CONNECTIVITY TEST ===');
    
    const testUrls = [
      'http://localhost:3001/health',
      'http://127.0.0.1:3001/health'
    ];

    for (const url of testUrls) {
      console.log(`Testing: ${url}`);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch(url, {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        clearTimeout(timeoutId);
        
        console.log(`${url} - Response:`, {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log(`${url} - Data:`, data);
          return { success: true, url, data };
        }
      } catch (error) {
        console.error(`${url} - Error:`, {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
    }
    
    return { success: false };
  };

  const handleStripeCheckout = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to book a session",
        variant: "destructive"
      });
      const currentUrl = location.pathname;
      const returnToUrl = encodeURIComponent(currentUrl);
      navigate(`/auth?returnTo=${returnToUrl}`);
      return;
    }

    if (!validateBookingForm()) return;

    setIsCreatingPayment(true);

    try {
      // Check if slot is still available before creating booking
      const dateKey = selectedDate!.toISOString().split('T')[0];
      const bookedSlotsForDate = bookedSlots[dateKey] || [];
      
      const isSlotStillAvailable = !bookedSlotsForDate.some(bookedSlot => 
        selectedTimeSlot!.start === bookedSlot.start && selectedTimeSlot!.end === bookedSlot.end
      );

      if (!isSlotStillAvailable) {
        toast({
          title: "Slot No Longer Available",
          description: "This time slot has been booked by another student. Please select a different slot.",
          variant: "destructive"
        });
        setSelectedTimeSlot(null);
        await fetchBookedSlots(); // Refresh the available slots
        return;
      }

      console.log('=== STRIPE CHECKOUT DEBUG ===');
      console.log('Current origin:', window.location.origin);
      console.log('User:', user);
      console.log('Selected date:', selectedDate);
      console.log('Selected time slot:', selectedTimeSlot);
      console.log('Selected subject:', selectedSubject);
      console.log('Tutor:', tutor);

      // First create the booking record
      const formattedDate = selectedDate!.toISOString().split('T')[0];
      
      const bookingData = {
        studentId: user.id,
        tutorId: tutor.id,
        date: formattedDate,
        startTime: selectedTimeSlot!.start,
        endTime: selectedTimeSlot!.end,
        status: 'pending',
        subject: selectedSubject,
        studentEmail: studentEmail.trim(),
        studentPhone: studentPhone.trim(),
        studentCountry: studentCountry,
        studentTimezone: studentTimezone, // Store the actual timezone used
        notes: '',
        paymentStatus: 'pending',
        price: getEffectiveHourlyRate(),
        pricingPlan: selectedPricingPlan,
        pricingLevel: selectedPricingPlan === 'level' ? selectedLevel : null
      };

      console.log('Creating booking with data:', JSON.stringify(bookingData, null, 2));
      
      // Ensure student profile exists before creating booking
      if (user?.role === 'student') {
        await ensureStudentProfileExists(user.id, studentEmail.trim(), studentPhone.trim(), studentCountry);
      }

      const createdBooking = await pb.collection('bookings').create(bookingData);
      console.log('Booking created successfully:', createdBooking);

      // Refresh booked slots to reflect the new booking
      await fetchBookedSlots();

      // Create Kashier checkout session
      const checkoutData = {
        bookingId: createdBooking.id,
        amount: getEffectiveHourlyRateInEGPWithPromo(),
        customerEmail: user.email || studentEmail.trim(),
        customerName: user.name || 'Student',
        tutorName: getTutorName(),
        subject: selectedSubject,
        date: selectedDate!.toLocaleDateString(),
        time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`,
      };

      console.log('Creating Kashier checkout with data:', checkoutData);

      const response = await kashierService.createCheckoutSession(checkoutData);

      console.log('Kashier response:', response);

      if (response.success) {
        // Redirect directly to Kashier payment page
        kashierService.redirectToPayment(response);
      } else {
        throw new Error(response.error || 'Failed to create checkout session');
      }

    } catch (error: any) {
      console.error('Kashier checkout error:', error);
      
      // Show a more helpful error message
      const errorMessage = error.message || "Failed to create payment session";
      
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handleAuthRedirect = () => {
    // Get the current tutor page URL to return to after auth
    const currentUrl = location.pathname; // e.g. "/tutor/0h673252l136ltd"
    const returnToUrl = encodeURIComponent(currentUrl);
    
    // Navigate to auth with return URL parameter
    navigate(`/auth?returnTo=${returnToUrl}`);
  };

  const handleBookWithPayment = () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to book a session",
        variant: "destructive"
      });
      const currentUrl = location.pathname;
      const returnToUrl = encodeURIComponent(currentUrl);
      navigate(`/auth?returnTo=${returnToUrl}`);
      return;
    }

    if (!validateBookingForm()) return;

    setShowMockPayment(true);
  };

  const handleTestKashier = async () => {
    console.log('🎯 HPP HANDLER CALLED!');
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to test payment",
        variant: "destructive"
      });
      return;
    }

    setIsCreatingHPPPayment(true);
    
    try {
      console.log('🔄 Step 1: Creating real test booking in PocketBase...');
      
      // Create a real booking in PocketBase first
      const formattedDate = selectedDate!.toISOString().split('T')[0];
      
      const bookingData = {
        studentId: user.id,
        tutorId: tutor?.id || '',
        date: formattedDate,
        startTime: selectedTimeSlot!.start,
        endTime: selectedTimeSlot!.end,
        status: 'pending',
        subject: selectedSubject,
        studentEmail: studentEmail.trim(),
        studentPhone: studentPhone.trim(),
        studentCountry: studentCountry,
        studentTimezone: studentTimezone,
        notes: '',
        paymentStatus: 'pending',
        price: getEffectiveHourlyRate(),
        originalPrice: getOriginalHourlyRate(),
        promoCode: appliedPromoDiscount?.discount?.code || '',
        promoDiscount: getPromoDiscountAmount() || 0,
        pricingPlan: selectedPricingPlan,
        pricingLevel: selectedPricingPlan === 'level' ? selectedLevel : null
      };

      console.log('📝 Creating booking with data:', bookingData);

      // Ensure student profile exists before creating booking
      if (user?.role === 'student') {
        await ensureStudentProfileExists(user.id, studentEmail.trim(), studentPhone.trim(), studentCountry);
      }

      const booking = await pb.collection('bookings').create(bookingData);
      console.log('✅ Booking created successfully! ID:', booking.id);

      // Increment promo code usage if applied
      if (appliedPromoDiscount?.isValid && appliedPromoDiscount.discount?.id) {
        await incrementDiscountUsage(appliedPromoDiscount.discount.id);
        console.log('✅ Promo code usage incremented for Kashier booking');
      }

      // Create tutor earnings record for Kashier booking (pending until payment confirmed)
      try {
        const tutorEmailForEarnings = tutor?.personalEmail || tutor?.expand?.user?.email || tutor?.email || '';
        const tutorUserIdForKashier = tutor?.user || tutor?.expand?.user?.id;
        await createTutorEarning(
          tutor.id,
          tutor?.name || 'Tutor',
          tutorEmailForEarnings,
          booking.id,
          user.name || 'Student',
          selectedSubject,
          selectedDate!.toISOString(),
          getEffectiveHourlyRateInEGP(), // Use EGP converted price
          'kashier',
          tutorUserIdForKashier
        );
        console.log('✅ Tutor earnings record created for Kashier booking');
      } catch (earningsError) {
        console.warn('⚠️ Failed to create tutor earnings record:', earningsError);
      }

      // Send email notification with only one admin email for Kashier
      console.log('📧 Step 1.5: Sending Kashier booking notification emails...');
      try {
        // Generate Jitsi room link
        const jitsiRoomLink = `https://meet.jit.si/tutornesta-${booking.id}`;
        
        const bookingEmailData = {
          id: booking.id,
          studentName: user.name || 'Student',
          studentEmail: studentEmail.trim(),
          tutorName: getTutorName(),
          tutorEmail: tutor?.personalEmail || tutor?.expand?.user?.email || tutor?.email || '',
          subject: selectedSubject,
          date: selectedDate!.toLocaleDateString(),
          time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`,
          status: 'pending',
          studentPhone: studentPhone.trim(),
          tutorPhone: tutor?.phoneNumber || tutor?.expand?.user?.phone || '',
          tutorTimezone: tutor?.timezone || '',
          studentTimezone: studentTimezone,
          startTime: selectedTimeSlot!.start,
          endTime: selectedTimeSlot!.end,
          jitsiRoomLink: jitsiRoomLink
        };
        
        // Send notification emails for Kashier payment with student, tutor, and Kashier-specific admin emails
        const emailResult = await EmailService.sendBookingNotification(
          bookingEmailData, 
          'kashier' // Use Kashier admin email list from PocketBase
        );
        
        if (emailResult.success) {
          console.log('✅ Kashier booking emails sent successfully');
        } else {
          console.warn('⚠️ Some Kashier booking emails failed:', emailResult.message);
        }
      } catch (emailError) {
        console.error('❌ Error sending Kashier booking emails:', emailError);
      }

      console.log('🔄 Step 2: Creating Kashier checkout session...');
      const checkoutData = {
        bookingId: booking.id,
        amount: getEffectiveHourlyRateInEGPWithPromo(),
        customerEmail: studentEmail.trim(),
        customerName: user.name || 'Student',
        tutorName: getTutorName(),
        subject: selectedSubject,
        date: selectedDate!.toLocaleDateString(),
        time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`,
      };

      console.log('💳 Creating Kashier checkout with data:', checkoutData);
      const response = await kashierService.createCheckoutSession(checkoutData);
      console.log('📨 Kashier test response:', response);

      if (response.success) {
        console.log('✅ Redirecting to Kashier payment page...');
        kashierService.redirectToPayment(response);
      } else {
        throw new Error(response.error || 'Failed to create test checkout session');
      }
    } catch (error: any) {
      console.error('❌ Test Kashier checkout error:', error);
      toast({
        title: "Test Payment Error",
        description: error.message || "Failed to create test payment session",
        variant: "destructive"
      });
    } finally {
      setIsCreatingHPPPayment(false);
    }
  };

  const handleTestKashierIframe = async () => {
    console.log('🎯 IFRAME HANDLER CALLED!');
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to test payment",
        variant: "destructive"
      });
      return;
    }

    console.log('✅ User authenticated, proceeding with iframe checkout');
    setIsCreatingIframePayment(true);
    
    try {
      // Create test booking data
      const testCheckoutData = {
        bookingId: 'test_iframe_' + Date.now(),
        amount: 100, // Test amount in EGP
        customerEmail: user.email || 'test@example.com',
        customerName: user.name || 'Test User',
        tutorName: getTutorName(),
        subject: 'Test Subject (Iframe)',
        date: new Date().toLocaleDateString(),
        time: '10:00 - 11:00',
      };

      console.log('Creating test Kashier iframe checkout with data:', testCheckoutData);

      const response = await kashierService.createCheckoutSession(testCheckoutData);

      console.log('Kashier iframe test response:', response);

      if (response.success) {
        // Open iframe payment
        kashierService.openIframePayment(response);
      } else {
        throw new Error(response.error || 'Failed to create test iframe checkout session');
      }
    } catch (error: any) {
      console.error('Test Kashier iframe checkout error:', error);
      toast({
        title: "Test Payment Error",
        description: error.message || "Failed to create test iframe payment session",
        variant: "destructive"
      });
    } finally {
      setIsCreatingIframePayment(false);
    }
  };

  const handleBookSession = async () => {
    // For free trial or demo booking without payment
    console.log('=== FREE BOOKING SESSION DEBUG ===');
    console.log('🚀 handleBookSession function called - isBooking:', isBooking);
    
    // Prevent double submissions
    if (isBooking) {
      console.log('⚠️ Already booking, ignoring duplicate click');
      return;
    }
    
    if (!user) {
      console.log('❌ No user found, redirecting to auth');
      toast({
        title: "Authentication Required",
        description: "Please log in to book a session",
        variant: "destructive"
      });
      const currentUrl = location.pathname;
      const returnToUrl = encodeURIComponent(currentUrl);
      navigate(`/auth?returnTo=${returnToUrl}`);
      return;
    }

    if (!validateBookingForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    console.log('✅ Starting free booking process...');
    console.log('User ID:', user.id);
    console.log('Tutor ID:', tutor.id);
    console.log('Selected date:', selectedDate);
    console.log('Selected time slot:', selectedTimeSlot);
    console.log('Selected subject:', selectedSubject);

    setIsBooking(true);
    
    try {
      console.log('🔍 Checking slot availability...');
      // Check if slot is still available before creating booking
      const dateKey = selectedDate!.toISOString().split('T')[0];
      const bookedSlotsForDate = bookedSlots[dateKey] || [];
      
      const isSlotStillAvailable = !bookedSlotsForDate.some(bookedSlot => 
        selectedTimeSlot!.start === bookedSlot.start && selectedTimeSlot!.end === bookedSlot.end
      );

      if (!isSlotStillAvailable) {
        console.log('❌ Slot no longer available');
        toast({
          title: "Slot No Longer Available",
          description: "This time slot has been booked by another student. Please select a different slot.",
          variant: "destructive"
        });
        setSelectedTimeSlot(null);
        // Don't refresh slots here to avoid potential cancellations
        setIsBooking(false);
        return;
      }

      console.log('✅ Slot is available, proceeding with booking creation...');

      // Track InitiateCheckout for customer service bookings (not Kashier - those are tracked with Purchase)
      trackInitiateCheckout([{
        id: tutor.id,
        title: tutor.name || 'Tutoring Session',
        price: getEffectiveHourlyRate(),
        quantity: 1
      }], getEffectiveHourlyRate(), {
        tutor_name: tutor.name || 'Unknown Tutor',
        student_name: user?.name || 'Unknown Student',
        student_email: studentEmail.trim(),
        subject: selectedSubject
      });

      // Ensure student profile exists before creating booking
      if (user?.role === 'student') {
        await ensureStudentProfileExists(user.id, studentEmail.trim(), studentPhone.trim(), studentCountry);
      }

      // Check PocketBase authentication status
      console.log('🔐 PocketBase auth check:');
      console.log('PocketBase auth store model:', pb.authStore.model);
      console.log('PocketBase auth token exists:', !!pb.authStore.token);
      console.log('PocketBase auth valid:', pb.authStore.isValid);
      
      const formattedDate = selectedDate!.toISOString().split('T')[0];
      console.log('Formatted date:', formattedDate);
      
      // Use the authenticated user's ID directly
      const pbUserId = user.id;
      console.log('Using user ID for booking:', pbUserId);
      
      const bookingData = {
        studentId: pbUserId,
        tutorId: tutor.id,
        date: formattedDate,
        startTime: selectedTimeSlot!.start,
        endTime: selectedTimeSlot!.end,
        status: 'pending',
        subject: selectedSubject,
        studentEmail: studentEmail.trim(),
        studentPhone: studentPhone.trim() || '',
        studentCountry: studentCountry,
        studentTimezone: studentTimezone, // Store the actual timezone used
        tutorTimezone: tutorTimezone, // Store tutor's timezone automatically
        notes: '',
        paymentStatus: 'pending',
        price: getEffectiveHourlyRate(),
        originalPrice: getOriginalHourlyRate(),
        promoCode: appliedPromoDiscount?.discount?.code || '',
        promoDiscount: getPromoDiscountAmount() || 0,
        pricingPlan: selectedPricingPlan,
        pricingLevel: selectedPricingPlan === 'level' ? selectedLevel : null
      };
      
      console.log('📝 About to create booking with data:', JSON.stringify(bookingData, null, 2));
      
      const createdBooking = await pb.collection('bookings').create(bookingData);
      console.log('✅ Booking created successfully:', createdBooking);

      // Increment promo code usage if applied
      if (appliedPromoDiscount?.isValid && appliedPromoDiscount.discount?.id) {
        await incrementDiscountUsage(appliedPromoDiscount.discount.id);
        console.log('✅ Promo code usage incremented');
      }

      // Create tutor earnings record for customer service booking
      try {
        const tutorEmailForEarnings = tutor.personalEmail || tutor.expand?.user?.email || tutor.email || '';
        const tutorUserIdForCS = tutor.user || tutor.expand?.user?.id;
        await createTutorEarning(
          tutor.id,
          tutor.name || 'Tutor',
          tutorEmailForEarnings,
          createdBooking.id,
          user.name || 'Student',
          selectedSubject,
          selectedDate!.toISOString(),
          getEffectiveHourlyRateInEGP(), // Use EGP converted price
          'regular',
          tutorUserIdForCS
        );
        console.log('✅ Tutor earnings record created for customer service booking');
      } catch (earningsError) {
        console.warn('⚠️ Failed to create tutor earnings record:', earningsError);
      }

      // Send email notifications immediately and asynchronously
      console.log('📧 === STARTING IMMEDIATE EMAIL NOTIFICATION ===');
      console.log('📧 Booking ID:', createdBooking.id);
      
      // Send booking notification emails with proper data
      try {
        console.log('📧 Sending booking notification emails...');
        console.log('📧 Email data being sent:', {
          studentEmail: studentEmail.trim(),
          tutorEmail: tutor.expand?.user?.email || tutor.email || '',
          adminEmail: 'support@tutornesta.com (hardcoded in EmailService)',
          bookingId: createdBooking.id
        });
        
        // Prepare booking data for emails with all required fields
        // Extract tutor email with better fallback logic
        const tutorEmail = tutor.expand?.user?.email || tutor.personalEmail || tutor.email || tutor.user || '';
        console.log('📧 Tutor email extraction debug:', {
          expandUserEmail: tutor.expand?.user?.email,
          personalEmail: tutor.personalEmail,
          tutorEmail: tutor.email,
          tutorUser: tutor.user,
          finalTutorEmail: tutorEmail
        });
        
        // Generate Jitsi room link
        const jitsiRoomLink = `https://meet.jit.si/tutornesta-${createdBooking.id}`;
        
        const bookingEmailData = {
          id: createdBooking.id,
          studentName: user.name || 'Student',
          studentEmail: studentEmail.trim(),
          studentPhone: studentPhone.trim(),
          tutorName: tutor.expand?.user?.name || tutor.name || 'Tutor',
          tutorEmail: tutorEmail,
          tutorPhone: tutor.phoneNumber || 'Not provided',
          subject: selectedSubject,
          date: formattedDate,
          time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`,
          status: 'pending',
          tutorTimezone: tutor.timezone || '',
          studentTimezone: studentTimezone || '',
          startTime: selectedTimeSlot!.start,
          endTime: selectedTimeSlot!.end,
          jitsiRoomLink: jitsiRoomLink
        };
        
        console.log('📧 Complete booking email data:', JSON.stringify(bookingEmailData, null, 2));
        
        const emailResult = await EmailService.sendBookingNotification(bookingEmailData, 'regular');
        
        console.log('📧 Email service complete result:', emailResult);
        
        if (emailResult.success) {
          console.log('✅ Booking emails sent successfully:', emailResult.results);
          console.log('📧 Detailed results breakdown:');
          emailResult.results?.forEach(result => {
            console.log('📧 Result:', result);
            if (result.includes('admin')) {
              console.log('🔍 ADMIN EMAIL RESULT:', result);
            }
          });
        } else {
          console.warn('⚠️ Some booking emails failed:', emailResult.message);
        }
      } catch (emailError) {
        console.error('📧 Email error:', emailError);
      }
      
      console.log('✅ Showing success message...');
      
      // Get display time in student's timezone
      const displayTime = studentCountry && tutorTimezone !== studentTimezone
        ? `${convertToStudentTime(selectedTimeSlot!.start, selectedDate!)} - ${convertToStudentTime(selectedTimeSlot!.end, selectedDate!)}`
        : `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`;
      
      toast({
        title: "Session Request Submitted!",
        description: `Your free ${selectedSubject} session request with ${getTutorName()} has been submitted for ${selectedDate!.toLocaleDateString()} at ${displayTime} (your timezone). Waiting for tutor confirmation.`,
      });

      console.log('🧭 Navigating to confirmation page...');
      // Set booking to false before navigation to prevent issues
      setIsBooking(false);
      navigate(`/booking-confirmation?bookingId=${createdBooking.id}`);
      onClose();
      
      // Refresh slots after successful completion (in background)
      setTimeout(() => {
        fetchBookedSlots().catch(console.error);
      }, 100);
      
    } catch (error: any) {
      console.error('=== BOOKING CREATION ERROR ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error details:', error.data);
      console.error('Error stack:', error.stack);
      
      let errorMessage = "Failed to book session. Please try again.";
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Booking Error",
        description: errorMessage,
        variant: "destructive"
      });
      
      setIsBooking(false);
    }
  };

  // Handle group booking submission
  const handleGroupBooking = async () => {
    console.log('=== GROUP BOOKING DEBUG ===');
    console.log('🚀 handleGroupBooking function called');
    
    if (isBooking) {
      console.log('⚠️ Already booking, ignoring duplicate click');
      return;
    }
    
    if (!user) {
      console.log('❌ No user found, redirecting to auth');
      toast({
        title: "Authentication Required",
        description: "Please log in to book a group session",
        variant: "destructive"
      });
      const currentUrl = location.pathname;
      const returnToUrl = encodeURIComponent(currentUrl);
      navigate(`/auth?returnTo=${returnToUrl}`);
      return;
    }

    if (!validateBookingForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    console.log('✅ Starting group booking process...');
    setIsBooking(true);
    
    try {
      // Check slot availability
      const dateKey = selectedDate!.toISOString().split('T')[0];
      const bookedSlotsForDate = bookedSlots[dateKey] || [];
      
      const isSlotStillAvailable = !bookedSlotsForDate.some(bookedSlot => 
        selectedTimeSlot!.start === bookedSlot.start && selectedTimeSlot!.end === bookedSlot.end
      );

      if (!isSlotStillAvailable) {
        console.log('❌ Slot no longer available');
        toast({
          title: "Slot No Longer Available",
          description: "This time slot has been booked. Please select a different slot.",
          variant: "destructive"
        });
        setSelectedTimeSlot(null);
        setIsBooking(false);
        return;
      }

      console.log('✅ Slot is available, creating group booking...');
      
      const formattedDate = selectedDate!.toISOString().split('T')[0];
      
      // Create the main booking (group leader's booking)
      const leaderBookingData = {
        studentId: user.id,
        tutorId: tutor.id,
        date: formattedDate,
        startTime: selectedTimeSlot!.start,
        endTime: selectedTimeSlot!.end,
        status: 'pending',
        subject: selectedSubject,
        studentEmail: groupLeaderEmail.trim(),
        studentPhone: groupLeaderPhone.trim(),
        studentCountry: studentCountry,
        studentTimezone: studentTimezone,
        tutorTimezone: tutorTimezone,
        notes: '',
        paymentStatus: 'pending',
        price: calculateGroupPrice().discountedPrice, // Use discounted group price
        is_group_booking: true,
        group_leader_email: groupLeaderEmail.trim(),
        group_leader_phone: groupLeaderPhone.trim(),
        group_size: groupSize,
        pricingPlan: selectedPricingPlan,
        pricingLevel: selectedPricingPlan === 'level' ? selectedLevel : null
      };
      
      console.log('📝 Creating leader booking:', leaderBookingData);
      const leaderBooking = await pb.collection('bookings').create(leaderBookingData);
      console.log('✅ Leader booking created:', leaderBooking.id);

      // Create invite records for each member
      const validMembers = memberEmails
        .map((email, index) => ({
          email: email.trim(),
          phone: memberPhones[index]?.trim() || ''
        }))
        .filter(member => member.email);
      
      console.log(`📧 Creating ${validMembers.length} invite records...`);
      
      // Generate Jitsi room link for group session
      const groupJitsiRoomLink = `https://meet.jit.si/tutornesta-${leaderBooking.id}`;
      
      const invitePromises = validMembers.map(async (member) => {
        const inviteToken = generateInviteToken();
        const inviteData = {
          booking_id: leaderBooking.id,
          member_email: member.email,
          member_phone: member.phone,
          invite_token: inviteToken,
          is_claimed: false,
          claimed_by_user_id: '' // Initialize as empty, will be set when claimed
        };
        
        try {
          const invite = await pb.collection('group_booking_invites').create(inviteData);
          console.log(`✅ Created invite for ${member.email}:`, invite.id);
          
          // Send invitation email with Jitsi room link
          const inviteUrl = `https://tutornesta.com/auth?invite=${inviteToken}`;
          await EmailService.sendGroupInviteEmail({
            memberEmail: member.email,
            leaderName: user.name || 'Group Leader',
            tutorName: tutor.name || getTutorName(),
            subject: selectedSubject,
            date: selectedDate!.toLocaleDateString(),
            time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`,
            inviteUrl,
            jitsiRoomLink: groupJitsiRoomLink
          });
          console.log(`📧 Sent invite email to ${member.email}`);
          
          return { success: true, email: member.email };
        } catch (error) {
          console.error(`❌ Error creating invite for ${member.email}:`, error);
          return { success: false, email: member.email, error };
        }
      });
      
      const inviteResults = await Promise.all(invitePromises);
      const successCount = inviteResults.filter(r => r.success).length;
      console.log(`✅ Created ${successCount}/${validMembers.length} invites`);

      // Send notification to tutor about group booking
      try {
        // Generate Jitsi room link
        const jitsiRoomLink = `https://meet.jit.si/tutornesta-${leaderBooking.id}`;
        
        const bookingEmailData = {
          id: leaderBooking.id,
          studentName: user.name || 'Group Leader',
          studentEmail: groupLeaderEmail.trim(),
          studentPhone: groupLeaderPhone.trim(),
          tutorName: tutor.expand?.user?.name || tutor.name || 'Tutor',
          tutorEmail: tutor.expand?.user?.email || tutor.personalEmail || tutor.email || '',
          tutorPhone: tutor.phoneNumber || 'Not provided',
          subject: selectedSubject,
          date: formattedDate,
          time: `${selectedTimeSlot!.start} - ${selectedTimeSlot!.end}`,
          status: 'pending',
          tutorTimezone: tutor.timezone || '',
          studentTimezone: studentTimezone || '',
          startTime: selectedTimeSlot!.start,
          endTime: selectedTimeSlot!.end,
          isGroupBooking: true,
          groupSize: groupSize,
          jitsiRoomLink: jitsiRoomLink
        };
        
        await EmailService.sendBookingNotification(bookingEmailData, 'regular');
        console.log('✅ Sent tutor notification');
      } catch (emailError) {
        console.error('📧 Error sending tutor notification:', emailError);
      }
      
      toast({
        title: "Group Booking Submitted!",
        description: `Your group session request has been submitted. Invitations sent to ${successCount} member(s).`,
      });

      setIsBooking(false);
      navigate(`/booking-confirmation?bookingId=${leaderBooking.id}`);
      onClose();
      
      // Refresh slots in background
      setTimeout(() => {
        fetchBookedSlots().catch(console.error);
      }, 100);
      
    } catch (error: any) {
      console.error('=== GROUP BOOKING ERROR ===', error);
      
      toast({
        title: "Group Booking Error",
        description: error.message || "Failed to create group booking. Please try again.",
        variant: "destructive"
      });
      
      setIsBooking(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    console.log('📅 Date selection attempted:', date);
    
    if (!date) {
      setSelectedDate(undefined);
      setSelectedTimeSlot(null);
      return;
    }
    
    // Check if date has any available slots
    const allSlots = getAvailableTimeSlotsForDate(date);
    const hasAvailableSlots = allSlots.some(slot => !slot.isBooked);
    
    if (!hasAvailableSlots && allSlots.length > 0) {
      // Date has tutor availability but all slots are booked
      console.log('❌ Date selection blocked - all slots booked');
      toast({
        title: "All Slots Booked",
        description: `All time slots are already booked for ${date.toLocaleDateString()}. Please try a different date or check back later.`,
        variant: "destructive"
      });
      return;
    }
    
    if (!isDateAvailable(date)) {
      console.log('❌ Date selection blocked - no tutor availability');
      toast({
        title: "Date Unavailable", 
        description: "This tutor is not available on this date. Please select another date.",
        variant: "destructive"
      });
      return;
    }
    
    console.log('✅ Date selected successfully:', date);
    setSelectedDate(date);
    setSelectedTimeSlot(null);
  };

  const availableTimeSlots = selectedDate ? getAvailableTimeSlotsForDate(selectedDate) : [];
  const validationStatus = getValidationStatus();

  // Check if tutor is frozen - show frozen modal instead (moved after all hooks)
  if (tutor?.isFrozen) {
    return (
      <FrozenTutorModal
        isOpen={isOpen}
        onClose={onClose}
        tutor={tutor}
      />
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-[95vw] max-w-4xl max-h-[80vh] overflow-y-auto overflow-x-hidden">
          {/* Support Contact Banner - Simplified */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm font-medium text-green-900" dir="rtl">
                إذا كنت بحاجة إلى مساعدة، تواصل معنا
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={() => {
                    // Use first contact from supportContacts if available, otherwise fallback to default
                    const phoneNumber = supportContacts.length > 0 
                      ? supportContacts[0].phone_number 
                      : '31616024319'; // Default fallback number
                    let cleanNumber = phoneNumber.replace(/[^0-9+]/g, '');
                    if (cleanNumber.startsWith('00')) {
                      cleanNumber = cleanNumber.substring(2);
                    }
                    cleanNumber = cleanNumber.replace('+', '');
                    window.open(`https://wa.me/${cleanNumber}`, '_blank');
                  }}
                  className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors h-auto"
                >
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </Button>
                <a 
                  href="mailto:support@tutornesta.com" 
                  className="text-sm font-semibold text-green-900 hover:text-green-700 underline"
                >
                  support@tutornesta.com
                </a>
              </div>
            </div>
          </div>

          <DialogHeader className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-8 w-8 rounded-full"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
            <DialogTitle className="flex items-center space-x-3 pr-10">
              <Avatar className="h-12 w-12">
                <AvatarImage src={getProfilePictureUrl()} />
                <AvatarFallback>
                  {getTutorName().split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="text-lg font-semibold">{getTutorName()}</div>
                <div className="text-sm text-muted-foreground">
                  {tutor?.subjects?.map(s => capitalizeSubject(s)).join(', ')}
                </div>
              </div>
            </DialogTitle>
            {!user && (
              <DialogDescription>
                Please log in to request a session
              </DialogDescription>
            )}
          </DialogHeader>


          <div className="flex flex-col items-center">
            {/* Login prompt for non-logged users */}
            {!user && (
              <Card className="border-blue-200 bg-blue-50 w-full max-w-md">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-800 mb-2">
                      Not logged in / لم تسجل الدخول
                    </div>
                    <div className="text-xs text-blue-600 mb-3">
                      Please log in to book sessions
                      <br />
                      يُرجى تسجيل الدخول لحجز الجلسات
                    </div>
                    <Button 
                      onClick={handleAuthRedirect}
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700 py-4 px-6"
                    >
                      Log In / Sign Up
                      <br />
                      تسجيل الدخول / إنشاء حساب
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Booking content - centered */}
            <div className="space-y-4 w-full max-w-lg">
              {/* Booking Type Tabs */}
              {user && (
                <>
                  {/* Centralized Header */}
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold">Start Booking</h3>
                    <p className="text-sm text-muted-foreground">ابدأ بالحجز</p>
                  </div>
                  
                  {/* Pricing Plan Selector */}
                  {availablePricingOptions.length > 1 && (
                    <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="h-4 w-4 text-primary" />
                          <Label className="text-sm font-medium">
                            Pricing Plan / <span className="text-muted-foreground">خطة التسعير</span>
                          </Label>
                        </div>
                        <Select 
                          value={selectedPricingPlan} 
                          onValueChange={(v) => setSelectedPricingPlan(v as PricingPlan)}
                        >
                          <SelectTrigger className="bg-background">
                            <SelectValue>
                              {availablePricingOptions.find(o => o.value === selectedPricingPlan)?.label || 'Select Plan'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-background z-[100]">
                            {availablePricingOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex items-center justify-between w-full gap-4">
                                  <span>{option.label} / {option.labelAr}</span>
                                  <span className="text-muted-foreground text-sm">
                                    {formatTutorPrice(option.price)}{option.priceLabel}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Level selector when "By Level" is selected */}
                        {selectedPricingPlan === 'level' && tutor?.levelRates && (
                          <div className="mt-3">
                            <Label className="text-xs text-muted-foreground mb-1 block">
                              Select Level / <span>اختر المستوى</span>
                            </Label>
                            <Select 
                              value={selectedLevel} 
                              onValueChange={(v) => setSelectedLevel(v as LevelOption)}
                            >
                              <SelectTrigger className="bg-background">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-background z-[100]">
                                {tutor.levelRates.beginner > 0 && (
                                  <SelectItem value="beginner">
                                    <div className="flex items-center justify-between w-full gap-4">
                                      <span>Beginner / مبتدئ</span>
                                      <span className="text-muted-foreground text-sm">
                                        {formatTutorPrice(tutor.levelRates.beginner)}
                                      </span>
                                    </div>
                                  </SelectItem>
                                )}
                                {tutor.levelRates.intermediate > 0 && (
                                  <SelectItem value="intermediate">
                                    <div className="flex items-center justify-between w-full gap-4">
                                      <span>Intermediate / متوسط</span>
                                      <span className="text-muted-foreground text-sm">
                                        {formatTutorPrice(tutor.levelRates.intermediate)}
                                      </span>
                                    </div>
                                  </SelectItem>
                                )}
                                {tutor.levelRates.advanced > 0 && (
                                  <SelectItem value="advanced">
                                    <div className="flex items-center justify-between w-full gap-4">
                                      <span>Advanced / متقدم</span>
                                      <span className="text-muted-foreground text-sm">
                                        {formatTutorPrice(tutor.levelRates.advanced)}
                                      </span>
                                    </div>
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {/* Session note */}
                        {selectedPricingPlan === 'session' && (
                          <p className="text-xs text-muted-foreground mt-2 text-center">
                            (الجلسة أكثر من ساعة ونصف)
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  
                  <Tabs value={bookingType} onValueChange={(v) => setBookingType(v as 'individual' | 'group')} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="individual" className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span className="flex flex-col items-center leading-tight">
                          <span>Individual Lesson</span>
                          <span className="text-xs opacity-80">درس فردي</span>
                        </span>
                      </TabsTrigger>
                      <TabsTrigger value="group" className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span className="flex flex-col items-center leading-tight">
                          <span>Group Lesson</span>
                          <span className="text-xs opacity-80">درس جماعي</span>
                        </span>
                      </TabsTrigger>
                    </TabsList>
                  
                   {/* Individual Booking Form */}
                  <TabsContent value="individual" className="space-y-4 mt-4">
                    {/* Contact Information & Country */}
                    <Card ref={contactInfoRef} className={(studentEmail.trim() && studentCountry) ? "border-green-200 bg-[hsl(var(--contact-bg))]" : "border-gray-200 bg-[hsl(var(--contact-bg))]"}>
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          Contact Information
                          {(studentEmail.trim() && studentCountry) && <CheckCircle className="h-4 w-4 ml-auto text-green-600" />}
                        </CardTitle>
                        {user?.role === 'tutor' && (
                          <Alert className="mt-2 bg-amber-50 border-amber-200">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-800 text-xs">
                              <span className="block">To update your contact info, please edit your profile and wait for admin approval.</span>
                              <span className="block mt-1 text-right" dir="rtl">لتحديث بيانات الاتصال، يُرجى تعديل ملفك الشخصي وانتظار موافقة المسؤول.</span>
                            </AlertDescription>
                          </Alert>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {!user ? (
                          <div className="space-y-3 text-center">
                            <p className="text-sm font-medium text-muted-foreground">
                              Please log in to fill your contact information.
                            </p>
                            <p className="text-sm text-muted-foreground">
                              يُرجى تسجيل الدخول لكتابة بيانات التواصل الخاصة بك.
                            </p>
                            <Button
                              size="sm"
                              onClick={handleAuthRedirect}
                            >
                              Log In / Sign Up
                              <br />
                              تسجيل الدخول / إنشاء حساب
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="space-y-2">
                              <Label htmlFor="studentCountry">Your Country *</Label>
                              <CountryCombobox
                                value={studentCountry}
                                onValueChange={user?.role === 'tutor' ? undefined : setStudentCountry}
                                placeholder="Select your country"
                                className={`${studentCountry ? "border-green-300" : ""} ${user?.role === 'tutor' ? "opacity-60 cursor-not-allowed" : ""}`}
                                disabled={user?.role === 'tutor'}
                              />
                              {studentCountry && (
                                <div className="text-sm text-green-600 flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" />
                                  Timezone: {studentTimezone}
                                </div>
                              )}
                            </div>
                            
                            {/* Timezone selector - always enabled for booking purposes */}
                            {studentCountry && hasMultipleTimezones(studentCountry) && (
                              <div className="space-y-2">
                                <Label htmlFor="studentTimezone" className="flex items-center gap-2">
                                  <Clock className="h-4 w-4" />
                                  Your Timezone *
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  Your country has multiple timezones. Please select yours for accurate booking times.
                                </p>
                                {user?.role === 'tutor' && (
                                  <p className="text-xs text-amber-600">
                                    (For this booking only / لهذا الحجز فقط)
                                  </p>
                                )}
                                <Select 
                                  value={studentTimezoneOverride} 
                                  onValueChange={setStudentTimezoneOverride}
                                >
                                  <SelectTrigger className={studentTimezoneOverride ? "border-green-300" : ""}>
                                    <SelectValue placeholder="Select your timezone">
                                      {studentTimezoneOverride && getTimezoneDisplayName(studentTimezoneOverride)}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[200px] overflow-y-auto bg-background z-[100]">
                                    {getCountryTimezones(studentCountry).map((tz) => (
                                      <SelectItem key={tz} value={tz}>
                                        {getTimezoneDisplayName(tz)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {studentTimezoneOverride && (
                                  <div className="text-sm text-green-600 flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    Timezone selected
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className="space-y-2">
                              <Label htmlFor="studentEmail">Email Address *</Label>
                              <Input
                                id="studentEmail"
                                type="email"
                                value={studentEmail}
                                onChange={(e) => setStudentEmail(e.target.value)}
                                placeholder="your.email@example.com"
                                className={`${studentEmail.trim() ? "border-green-300" : ""} ${user?.role === 'tutor' ? "opacity-60 cursor-not-allowed" : ""}`}
                                disabled={user?.role === 'tutor'}
                                readOnly={user?.role === 'tutor'}
                              />
                              {studentEmail.trim() && (
                                <div className="text-sm text-green-600 flex items-center">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Email provided
                                </div>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="studentPhone">Phone Number *</Label>
                              <div className="relative flex items-center">
                                <span className="absolute left-3 text-muted-foreground font-medium">
                                  {getPhoneCode(studentCountry)}
                                </span>
                                <Input
                                  id="studentPhone"
                                  type="tel"
                                  value={studentPhone}
                                  onChange={(e) => {
                                    if (user?.role === 'tutor') return; // Prevent changes for tutors
                                    const value = e.target.value;
                                    setStudentPhone(value);
                                    
                                    // Validate phone number for selected country (strong rules for Egypt & USA)
                                    if (value.trim() && studentCountry) {
                                      const result = validatePhoneByCountry(value, studentCountry);
                                      if (result.valid) {
                                        setPhoneError('');
                                      } else {
                                        setPhoneError(result.message || 'Invalid phone number');
                                      }
                                    } else {
                                      setPhoneError('');
                                    }
                                  }}
                                  placeholder={studentCountry === 'EG' ? "01XX XXX XXXX" : studentCountry === 'US' ? "555 123 4567" : "123 456 7890"}
                                  className={`pl-16 ${phoneError ? "border-red-500" : studentPhone.trim() && !phoneError ? "border-green-300" : ""} ${user?.role === 'tutor' ? "opacity-60 cursor-not-allowed" : ""}`}
                                  disabled={user?.role === 'tutor'}
                                  readOnly={user?.role === 'tutor'}
                                />
                              </div>
                              {phoneError && (
                                <div className="text-sm text-red-600 flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {phoneError}
                                </div>
                              )}
                              {studentPhone.trim() && !phoneError && (
                                <div className="text-sm text-green-600 flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" />
                                  Valid phone number
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Group Booking Form */}
                  <TabsContent value="group" className="space-y-4 mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Group Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Group Size */}
                        <div className="space-y-2">
                          <Label htmlFor="groupSize">Total Group Size (including you) *</Label>
                          <Input
                            id="groupSize"
                            type="number"
                            min="2"
                            value={groupSize}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 2;
                              setGroupSize(value);
                              // Adjust member arrays to groupSize - 1 (excluding leader)
                              const neededMembers = value - 1;
                              if (neededMembers > memberEmails.length) {
                                setMemberEmails([...memberEmails, ...Array(neededMembers - memberEmails.length).fill('')]);
                                setMemberPhones([...memberPhones, ...Array(neededMembers - memberPhones.length).fill('')]);
                              } else if (neededMembers < memberEmails.length) {
                                setMemberEmails(memberEmails.slice(0, neededMembers));
                                setMemberPhones(memberPhones.slice(0, neededMembers));
                              }
                            }}
                            placeholder="2"
                          />
                          <p className="text-xs text-muted-foreground">
                            This includes you as the group leader
                          </p>
                          {groupSize > 10 && (
                            <Alert>
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>
                                We recommend keeping group size under 10 for better learning experience.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>

                        {/* Group Leader Contact */}
                        <div className="space-y-2">
                          <Label htmlFor="groupLeaderEmail">Group Leader Email *</Label>
                          <Input
                            id="groupLeaderEmail"
                            type="email"
                            value={groupLeaderEmail}
                            onChange={(e) => setGroupLeaderEmail(e.target.value)}
                            placeholder="leader@example.com"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="groupLeaderPhone">Group Leader Phone *</Label>
                          <Input
                            id="groupLeaderPhone"
                            type="tel"
                            value={groupLeaderPhone}
                            onChange={(e) => setGroupLeaderPhone(e.target.value)}
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>

                        {/* Country Selection */}
                        <div className="space-y-2">
                          <Label htmlFor="groupCountry">Country *</Label>
                          <CountryCombobox
                            value={studentCountry}
                            onValueChange={setStudentCountry}
                            placeholder="Select your country"
                          />
                        </div>

                        {/* Member Emails and Phones */}
                        <div className="space-y-2">
                          <Label>Other Member Details ({memberEmails.filter(e => e.trim()).length}/{groupSize - 1})</Label>
                          <p className="text-xs text-muted-foreground">
                            Enter email and phone for the other {groupSize - 1} member{groupSize - 1 !== 1 ? 's' : ''} (excluding yourself). Each will receive an invitation.
                          </p>
                          <div className="space-y-3">
                            {memberEmails.map((email, index) => (
                              <Card key={index} className="p-3">
                                <div className="space-y-2">
                                  <Label className="text-xs">Member {index + 1}</Label>
                                  <div className="flex gap-2">
                                    <div className="flex-1 space-y-2">
                                      <Input
                                        type="email"
                                        value={email}
                                        onChange={(e) => handleMemberEmailChange(index, e.target.value)}
                                        placeholder="Email address"
                                      />
                                      <Input
                                        type="tel"
                                        value={memberPhones[index] || ''}
                                        onChange={(e) => handleMemberPhoneChange(index, e.target.value)}
                                        placeholder="Phone number"
                                      />
                                    </div>
                                    {memberEmails.length > 1 && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveMemberEmail(index)}
                                        className="mt-0"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </Card>
                            ))}
                            {memberEmails.length < (groupSize - 1) && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAddMemberEmail}
                                className="w-full"
                              >
                                + Add Member
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Group Pricing Display */}
                    <Card className="border-green-200 bg-green-50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-green-900">
                          <CreditCard className="h-4 w-4" />
                          Group Pricing
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Tutor Rate per Hour:</span>
                            <PriceWithUsdBadge
                              formattedPrice={formatTutorPrice(calculateGroupPrice().baseRate)}
                              usdPrice={calculateGroupPrice().baseRate}
                              size="sm"
                              textColorClass="text-gray-800"
                            />
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Group Size:</span>
                            <span className="font-medium">{groupSize} members</span>
                          </div>
                          <div className="flex justify-between items-center text-sm text-gray-500 line-through">
                            <span>Total Before Discount:</span>
                            <span>{formatTutorPrice(calculateGroupPrice().totalBeforeDiscount)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-green-600 font-semibold">
                            <span>Group Discount (30%):</span>
                            <span>-{formatTutorPrice(calculateGroupPrice().savings)}</span>
                          </div>
                          <div className="pt-2 border-t border-green-300">
                            <div className="flex justify-between items-center text-lg font-bold text-green-900">
                              <span>Total Price:</span>
                              <PriceWithUsdBadge
                                formattedPrice={formatTutorPrice(calculateGroupPrice().discountedPrice)}
                                usdPrice={calculateGroupPrice().discountedPrice}
                                size="md"
                                textColorClass="text-green-900"
                              />
                            </div>
                            <p className="text-xs text-green-700 mt-1">
                              Save {formatTutorPrice(calculateGroupPrice().savings)} with group booking!
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
                </>
              )}
              
              {/* Show contact info for non-logged-in users (existing behavior) */}
              {!user && (
                <Card ref={contactInfoRef} className={(studentEmail.trim() && studentCountry) ? "border-green-200 bg-[hsl(var(--contact-bg))]" : "border-gray-200 bg-[hsl(var(--contact-bg))]"}>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Contact Information
                      {(studentEmail.trim() && studentCountry) && <CheckCircle className="h-4 w-4 ml-auto text-green-600" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="studentCountry">Your Country *</Label>
                      <CountryCombobox
                        value={studentCountry}
                        onValueChange={setStudentCountry}
                        placeholder="Select your country"
                        className={studentCountry ? "border-green-300" : ""}
                      />
                      {studentCountry && (
                        <div className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Timezone: {studentTimezone}
                        </div>
                      )}
                    </div>
                    
                    {/* Timezone selector - only show for countries with multiple timezones */}
                    {studentCountry && hasMultipleTimezones(studentCountry) && (
                      <div className="space-y-2">
                        <Label htmlFor="studentTimezone" className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Your Timezone *
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Your country has multiple timezones. Please select yours for accurate booking times.
                        </p>
                        <Select 
                          value={studentTimezoneOverride} 
                          onValueChange={setStudentTimezoneOverride}
                        >
                          <SelectTrigger className={studentTimezoneOverride ? "border-green-300" : ""}>
                            <SelectValue placeholder="Select your timezone">
                              {studentTimezoneOverride && getTimezoneDisplayName(studentTimezoneOverride)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px] overflow-y-auto bg-background z-[100]">
                            {getCountryTimezones(studentCountry).map((tz) => (
                              <SelectItem key={tz} value={tz}>
                                {getTimezoneDisplayName(tz)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {studentTimezoneOverride && (
                          <div className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Timezone selected
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="studentEmail">Email Address *</Label>
                      <Input
                        id="studentEmail"
                        type="email"
                        value={studentEmail}
                        onChange={(e) => setStudentEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        className={studentEmail.trim() ? "border-green-300" : ""}
                      />
                      {studentEmail.trim() && (
                        <div className="text-sm text-green-600 flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Email provided
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="studentPhone">Phone Number *</Label>
                      <Input
                        id="studentPhone"
                        type="tel"
                        value={studentPhone}
                        onChange={(e) => setStudentPhone(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Subject Selection */}
              {user && (
                <Card className={selectedSubject ? "border-green-200 bg-[hsl(var(--subject-bg))]" : "border-gray-200 bg-[hsl(var(--subject-bg))]"}>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-2" />
                      {!isLocalCoach && tutor?.subjects?.length === 1 ? 'Subject' : 'Select Subject'}
                      {selectedSubject && <CheckCircle className="h-4 w-4 ml-auto text-green-600" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {/* Single subject - just display it */}
                      {!isLocalCoach && tutor?.subjects?.length === 1 ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                          <Badge variant="secondary" className="text-sm">
                            {capitalizeSubject(tutor.subjects[0])}
                          </Badge>
                        </div>
                      ) : (
                        <>
                          <Label htmlFor="subject">Choose a subject for your session</Label>
                          <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                            <SelectTrigger className={selectedSubject ? "border-green-300" : ""}>
                              <SelectValue placeholder="Select a subject" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border shadow-lg z-50">
                              {isLocalCoach && initialSubject === 'Travel' ? (
                                // Travel coaching subjects
                                <>
                                  <SelectItem value="Travel">Travel</SelectItem>
                                  <SelectItem value="City Tours">City Tours</SelectItem>
                                  <SelectItem value="Adventure Tours">Adventure Tours</SelectItem>
                                  <SelectItem value="Cultural Tours">Cultural Tours</SelectItem>
                                  <SelectItem value="Food Tours">Food Tours</SelectItem>
                                  <SelectItem value="Historical Tours">Historical Tours</SelectItem>
                                  <SelectItem value="Nature Tours">Nature Tours</SelectItem>
                                  <SelectItem value="Local Experiences">Local Experiences</SelectItem>
                                </>
                              ) : isLocalCoach && initialSubject === 'Music' ? (
                                // Music coaching subjects
                                <>
                                  <SelectItem value="Music">Music</SelectItem>
                                  <SelectItem value="Piano">Piano</SelectItem>
                                  <SelectItem value="Guitar">Guitar</SelectItem>
                                  <SelectItem value="Violin">Violin</SelectItem>
                                  <SelectItem value="Drums">Drums</SelectItem>
                                  <SelectItem value="Voice">Voice/Singing</SelectItem>
                                  <SelectItem value="Music Theory">Music Theory</SelectItem>
                                </>
                              ) : isLocalCoach ? (
                                // Fitness coaching subjects
                                <>
                                  <SelectItem value="Fitness">Fitness</SelectItem>
                                  <SelectItem value="Bodybuilding">Bodybuilding</SelectItem>
                                </>
                              ) : (
                                // Regular tutor subjects
                                tutor?.subjects?.map((subject, index) => (
                                  <SelectItem key={index} value={subject}>
                                    {capitalizeSubject(subject)}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {selectedSubject && (
                            <div className="text-sm text-green-600 flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Selected: {capitalizeSubject(selectedSubject)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className={selectedDate ? "border-green-200 bg-[hsl(var(--date-bg))]" : "border-gray-200 bg-[hsl(var(--date-bg))]"}>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Select Date
                    {selectedDate && <CheckCircle className="h-4 w-4 ml-auto text-green-600" />}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!studentCountry ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <Globe className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">
                          Please select your country first to see available dates in your timezone
                        </p>
                      </div>
                    </div>
                  ) : isLoadingBookings ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                        <div className="text-sm text-gray-500">Loading available dates...</div>
                      </div>
                    </div>
                  ) : (
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      showOutsideDays={false}
                      disabled={(date) => {
                        const earliestDate = getEarliestBookableDate();
                        return (
                          date < earliestDate || 
                          !user || 
                          isLoadingBookings || 
                          !hasTutorAvailability(date)
                        );
                      }}
                      modifiers={{
                        booked: (date) => isDateFullyBooked(date),
                        available: (date) => {
                          const earliestDate = getEarliestBookableDate();
                          return hasTutorAvailability(date) && !isDateFullyBooked(date) && date >= earliestDate;
                        },
                        selected: (date) => selectedDate ? date.toDateString() === selectedDate.toDateString() : false
                      }}
                      modifiersStyles={{
                        booked: {
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          opacity: 0.7
                        },
                        available: {
                          backgroundColor: '#d1fae5',
                          color: '#065f46',
                          fontWeight: '500'
                        },
                        selected: {
                          backgroundColor: 'hsl(var(--primary))',
                          color: 'white',
                          fontWeight: 'bold',
                          border: '2px solid hsl(var(--primary))',
                          boxShadow: '0 0 0 2px hsl(var(--primary) / 0.3)'
                        }
                      }}
                      className="rounded-md border w-full pointer-events-auto"
                      key={`calendar-${Object.keys(bookedSlots).length}-${isLoadingBookings}`}
                    />
                  )}
                  {selectedDate && (
                    <div className="mt-2 text-sm text-green-600 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Selected: {selectedDate.toLocaleDateString()}
                    </div>
                  )}
                  
                  {/* Calendar Legend */}
                  <div className="mt-3 text-xs text-gray-600 space-y-1">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-100 border border-green-300 rounded mr-1"></div>
                        <span>Available dates</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-primary text-white rounded mr-1 flex items-center justify-center font-bold" style={{fontSize: '8px'}}>✓</div>
                        <span>Selected date</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-100 border border-red-300 rounded mr-1"></div>
                        <span>Fully booked</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-gray-200 border border-gray-400 rounded mr-1"></div>
                        <span>Unavailable</span>
                      </div>
                    </div>
                  </div>
                  {!user && (
                    <button 
                      onClick={handleAuthRedirect}
                      className="mt-2 text-sm text-yellow-600 hover:text-yellow-700 text-center space-y-1 cursor-pointer hover:underline w-full group"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span>Log in to select dates</span>
                        <ArrowRight className="h-4 w-4 animate-pulse group-hover:translate-x-1 transition-transform" />
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span>سجّل الدخول لاختيار التواريخ</span>
                        <ArrowRight className="h-4 w-4 animate-pulse group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>
                  )}
                </CardContent>
              </Card>

              {selectedDate && user && (
                <Card className={selectedTimeSlot ? "border-green-200" : "border-gray-200"}>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Available Times
                      {selectedTimeSlot && <CheckCircle className="h-4 w-4 ml-auto text-green-600" />}
                    </CardTitle>
                  </CardHeader>
                   <CardContent>
                     {isLoadingBookings ? (
                       <div className="text-center py-4 text-gray-500">
                         Loading available slots...
                       </div>
                     ) : availableTimeSlots.length > 0 ? (
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                         {availableTimeSlots.map((slot, index) => {
                           // Convert time to student's timezone if different
                           const displayStart = studentCountry && tutorTimezone !== studentTimezone 
                             ? convertToStudentTime(slot.start, selectedDate!)
                             : slot.start;
                           const displayEnd = studentCountry && tutorTimezone !== studentTimezone 
                             ? convertToStudentTime(slot.end, selectedDate!)
                             : slot.end;
                           
                            return (
                             <Button
                               key={index}
                               variant={
                                 slot.isBooked 
                                   ? "secondary" 
                                   : (selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end)
                                     ? "default" 
                                     : "outline"
                               }
                               size="sm"
                               disabled={slot.isBooked}
                               onPointerDown={(e) => {
                                 if (!slot.isBooked) {
                                   e.preventDefault();
                                   console.log('Time slot selected:', slot);
                                   setSelectedTimeSlot(slot);
                                 }
                               }}
                               className={`justify-start transition-all duration-200 touch-manipulation active:scale-95 ${
                                 slot.isBooked 
                                   ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500' 
                                   : (selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end)
                                     ? 'bg-primary text-primary-foreground shadow-md border-primary'
                                     : 'hover:bg-primary/10 hover:border-primary/20 active:bg-primary/20'
                               }`}
                             >
                              {displayStart} - {displayEnd}
                              {slot.isBooked && <span className="ml-2 text-xs">(Booked)</span>}
                            </Button>
                            );
                         })}
                       </div>
                     ) : selectedDate ? (
                       <div className="text-center py-4 text-gray-500">
                         No slots available for this date
                       </div>
                     ) : (
                       <div className="text-center py-4 text-gray-500">
                         Please select a date first
                       </div>
                     )}
                     {selectedTimeSlot && (
                       <div className="mt-2 text-sm text-green-600 flex items-center">
                         <CheckCircle className="h-3 w-3 mr-1" />
                         Selected: {studentCountry && tutorTimezone !== studentTimezone 
                           ? `${convertToStudentTime(selectedTimeSlot.start, selectedDate!)} - ${convertToStudentTime(selectedTimeSlot.end, selectedDate!)}`
                           : `${selectedTimeSlot.start} - ${selectedTimeSlot.end}`
                         }
                       </div>
                      )}
                     
                      {/* Timezone conversion display */}
                      {selectedTimeSlot && (
                        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex flex-col gap-1 text-sm text-blue-700">
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4" />
                              <span className="font-medium">Timezone info:</span>
                            </div>
                            <div className="ml-6 space-y-1">
                              <div>
                              {isLocalCoach && localCoachData?.timezone ? 'Coach' : 'Tutor'} time: {selectedTimeSlot.start} - {selectedTimeSlot.end} 
                                ({isLocalCoach && localCoachData?.timezone ? localCoachData.timezone : tutorTimezone})
                              </div>
                              {((isLocalCoach && localCoachData?.timezone ? localCoachData.timezone : tutorTimezone) !== studentTimezone) && studentCountry && (
                                <div>Your time: {getLocalTimeDisplay()}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                   </CardContent>
                 </Card>
               )}
            </div>
          </div>

          {/* Promo Code Section */}
          {user && selectedTimeSlot && (
            <Card className="mb-4">
              <CardContent className="pt-4">
                <PromoCodeInput
                  orderAmount={getEffectiveHourlyRateInEGPBeforePromo()}
                  orderType="bookings"
                  onDiscountApplied={setAppliedPromoDiscount}
                />
                {appliedPromoDiscount?.isValid && (
                  <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Original Price:</span>
                      <span className="line-through text-muted-foreground">
                        {getEffectiveHourlyRateInEGP()} EGP
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-green-600">
                      <span>Promo Discount:</span>
                      <span>-{getPromoDiscountAmount()} EGP</span>
                    </div>
                    <div className="flex justify-between items-center font-semibold mt-1 pt-1 border-t border-green-200">
                      <span>Final Price:</span>
                      <span className="text-green-700">{getEffectiveHourlyRateInEGPWithPromo()} EGP</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Methods Accepted - Collapsible */}
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full justify-between mb-2"
                dir={selectedCurrency.code === 'EGP' ? 'rtl' : 'ltr'}
              >
                <span className="flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  {selectedCurrency.code === 'EGP' ? 'طرق الدفع المقبولة' : 'Payment Methods Accepted'}
                </span>
                <ChevronDown className="h-4 w-4 transition-transform duration-200" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mb-4 p-3 bg-card border rounded-lg" dir={selectedCurrency.code === 'EGP' ? 'rtl' : 'ltr'}>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 p-2 rounded-md bg-accent/50">
                    <Wallet className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium">InstaPay</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-accent/50">
                    <Wallet className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium">Fawry</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-accent/50">
                    <Phone className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium">Vodafone Cash</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-accent/50">
                    <Smartphone className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium">Orange Cash</span>
                  </div>
                </div>
                
                {/* Alternative payment methods note */}
                <div className="mt-3 p-2 rounded-md bg-muted/30 border border-muted">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {selectedCurrency.code === 'EGP' 
                      ? <>إذا لم تكن طريقة الدفع المفضلة لديك مدرجة أعلاه، فلا تتردد في الاتصال بنا. سنعمل معك لإيجاد حل دفع مناسب يلبي احتياجاتك. <a href="mailto:support@tutornesta.com" className="text-primary hover:underline font-medium">support@tutornesta.com</a></>
                      : <>If your preferred payment method is not listed above, please don&apos;t hesitate to contact us. We will work with you to find a suitable payment solution that meets your needs. <a href="mailto:support@tutornesta.com" className="text-primary hover:underline font-medium">support@tutornesta.com</a></>
                    }
                  </p>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Form Validation Status Alert - Moved before booking button */}
          {user && !validationStatus.isValid && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 space-y-2">
                <div>
                  <strong>Complete these steps to proceed:</strong>
                  <ul className="mt-1 ml-4 list-disc">
                    {validationStatus.missingFields.map((field, index) => (
                      <li key={index}>
                        {field === 'phone number' ? 'Add phone number' : `Select a ${field}`}
                      </li>
                    ))}
                  </ul>
                </div>
                <div dir="rtl">
                  <strong>أكمل هذه الخطوات للمتابعة:</strong>
                  <ul className="mt-1 mr-4 list-disc">
                    {validationStatus.missingFields.map((field, index) => {
                      const arabicFields: Record<string, string> = {
                        'subject': 'مادة',
                        'country': 'بلد',
                        'date': 'تاريخ',
                        'time slot': 'وقت الجلسة',
                        'phone number': 'رقم الهاتف'
                      };
                      const arabicAction = field === 'phone number' ? 'أضف' : 'اختر';
                      return (
                        <li key={index}>{arabicAction} {arabicFields[field] || field}</li>
                      );
                    })}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Success Status Alert */}
          {user && validationStatus.isValid && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Ready to book!</strong> All required information has been provided.
              </AlertDescription>
            </Alert>
          )}

          {/* Booking actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} className="order-3 sm:order-1">
              Cancel
            </Button>
            <div className="space-y-3 flex-1 order-1 sm:order-2">
              {/* Free First Session Guidance */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3 text-center">
                <p className="text-sm text-emerald-800 font-medium" dir="rtl">
                  🎁 إذا كنت ترغب في الحصول على جلستك التجريبية المجانية الأولى، احجز باستخدام زر خدمة العملاء وسنتواصل معك
                </p>
                <p className="text-sm text-emerald-700 mt-1">
                  🎁 Want your first free trial session? Book using the customer service button and we will contact you
                </p>
              </div>
              
              {/* Kashier Payment Button - Available for all users */}
              <Button 
                type="button"
                onClick={handleTestKashier}
                disabled={!validationStatus.isValid || isCreatingHPPPayment}
                variant="outline"
                className="w-full flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm border-orange-500 text-orange-600 hover:bg-orange-50 overflow-hidden min-h-[44px]"
              >
                <CreditCard className="h-4 w-4 flex-shrink-0" />
                {isCreatingHPPPayment 
                  ? 'Loading...' 
                  : selectedCurrency.code === 'EGP'
                    ? <span className="flex items-center gap-1 flex-wrap justify-center">
                        <span className="text-blue-600 font-semibold">Book now</span>
                        <span>- احجز الآن وادفع</span>
                        <span className="font-bold text-orange-600">{formatEGPAmount(getEffectiveHourlyRateInEGPWithPromo())}</span>
                        <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-semibold">
                          ≈${Math.round(getEffectiveHourlyRateInEGPWithPromo() / egpRate)}
                        </span>
                      </span>
                    : <span className="flex items-center gap-1 flex-wrap justify-center">
                        <span className="text-blue-600 font-semibold">Book now</span>
                        <span>and pay</span>
                        <span className="font-bold text-orange-600">{formatTutorPrice(getEffectiveHourlyRate())}</span>
                        <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-semibold">
                          ≈${Math.round(getEffectiveHourlyRate())}
                        </span>
                      </span>
                }
              </Button>
              
              {/* Payment Help Notification - Clickable */}
              <button 
                onClick={bookingType === 'group' ? handleGroupBooking : handleBookSession}
                disabled={!validationStatus.isValid || isBooking}
                className="w-full bg-blue-50 border border-blue-200 rounded-lg p-3 text-right hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
                dir="rtl"
              >
                <p className="text-sm text-blue-900 leading-relaxed">
                  إن كنت تحتاج إلى أي مساعدة في الدفع، يُرجى الضغط هنا، وسيقوم أحد موظفي خدمة العملاء بالتواصل معك هاتفيًا أو عبر تطبيق واتساب خلال الـ ٢٤ ساعة القادمة.
                </p>
                <p className="text-sm text-blue-800 font-semibold mt-2">
                  معلومة: يُرجى التأكّد من صحة رقم الهاتف الذي أدخلته
                </p>
              </button>
              
              <Button 
                onClick={bookingType === 'group' ? handleGroupBooking : handleBookSession}
                disabled={!validationStatus.isValid || isBooking}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {bookingType === 'group' ? <Users className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                {isBooking ? 'Submitting...' : bookingType === 'group' ? 'Book Group Session' : 'احجز الآن بمساعدة خدمة العملاء'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mock Payment Modal */}
      <MockPaymentModal
        isOpen={showMockPayment}
        onClose={() => setShowMockPayment(false)}
        bookingData={{
          tutorName: getTutorName(),
          subject: selectedSubject,
          date: selectedDate?.toLocaleDateString() || '',
          time: selectedTimeSlot ? `${selectedTimeSlot.start} - ${selectedTimeSlot.end}` : '',
          amount: getEffectiveHourlyRate()
        }}
        onPaymentComplete={handleMockPaymentComplete}
      />

      {/* Floating Login Button */}
      {!user && isOpen && (
        <Button
          onClick={handleAuthRedirect}
          className="fixed left-4 bottom-4 z-[100] shadow-lg animate-fade-in"
          size="sm"
        >
          <User className="h-4 w-4 mr-2" />
          Log In
        </Button>
      )}
    </>
  );
};

export default BookingModal;
